import React, { useState, useEffect, useCallback, useRef } from 'react';
import './PracticeDetailsPage.css';
import PayerNodePicker from '../components/PayerNodePicker';
import IntegrationsFeaturesPanel from '../components/IntegrationsFeaturesPanel';
import ApiKeysPanel from '../components/ApiKeysPanel';
import { invalidateCollectionsSettings } from '../features/collections';
import useCapabilities, { invalidateCapabilities } from '../hooks/useCapabilities';
import { SubmissionCard } from '../features/queue';
import BillingSettingsForm from '../features/billingSettings/BillingSettingsForm';
import CodingStandardsSection from '../features/codingStandards/CodingStandardsSection';
import WeeklyReportsCard from '../features/weeklyReports/WeeklyReportsCard';
import ClientFeedbackCard from '../features/clientFeedback/ClientFeedbackCard';
import { DataFeedsTable } from '../features/data';
import PracticeLogoCard from '../features/practiceLogo/PracticeLogoCard';
import { TemplatesTab, SmsNumberCard } from '../features/outreach';
import ContactsCard from '../features/clientTexts';
import PayerIdInput from '../components/PayerIdInput';
import FeeSchedulePage from '../features/feeSchedule/FeeSchedulePage';
import SheetConnectorCard from '../features/sheet/SheetConnectorCard';
import PracticeSetupWizard from '../components/PracticeSetupWizard';

const API_URL = process.env.REACT_APP_API_URL || '';

// Contract Details (the Atlas Rate field) hidden per David, 2026-07-27.
// The section was already invisible to clients via the readOnly guard, so this
// hides it from admins and billers too. Kept as a flag rather than deleted:
// flip to true to bring the section back exactly as it was.
// NOTE: this is the same practices.collections_fee_pct that the Admin screen's
// Payments tab also edits, and that tab is hidden too, so with both off there
// is no UI left that can read or set the rate.
const SHOW_CONTRACT_DETAILS = false;

const PROVIDER_TYPES = [
  'MD', 'NP', 'APRN', 'PA', 'PT', 'OT', 'DC',
  'SLP', 'LCSW', 'Psychologist', 'RD', 'DPM', 'Other'
];
// A home health agency's own Type 2 NPI is the billing and rendering provider on its
// 837I, so that practice may hold one provider row of this type (the server decides:
// the NPI lookup answers `allowed`, and the save is refused elsewhere).
const ORGANIZATION_PROVIDER_TYPE = 'Organization';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
  'DC','PR','VI','GU','AS','MP',
];

function validateNpi(npi) {
  if (!npi) return null; // optional
  const digits = npi.replace(/\D/g, '');
  if (digits.length !== 10) return 'NPI must be exactly 10 digits';
  // Luhn check (prefix 80840 per CMS spec)
  const full = '80840' + digits;
  let sum = 0;
  for (let i = full.length - 1; i >= 0; i--) {
    let d = parseInt(full[i], 10);
    if ((full.length - i) % 2 === 0) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
  }
  if (sum % 10 !== 0) return 'Invalid NPI (failed check digit)';
  return null;
}

function validateTaxId(taxId) {
  if (!taxId) return null; // optional
  const digits = taxId.replace(/\D/g, '');
  if (digits.length !== 9) return 'Tax ID must be 9 digits (XX-XXXXXXX)';
  return null;
}

function validateZip(zip) {
  if (!zip) return null; // optional
  const digits = zip.replace(/\D/g, '');
  if (digits.length !== 5 && digits.length !== 9) return 'ZIP must be 5 or 9 digits';
  return null;
}

function formatTaxId(value) {
  const digits = value.replace(/\D/g, '').slice(0, 9);
  if (digits.length > 2) return digits.slice(0, 2) + '-' + digits.slice(2);
  return digits;
}

function formatZip(value) {
  const digits = value.replace(/\D/g, '').slice(0, 9);
  if (digits.length > 5) return digits.slice(0, 5) + '-' + digits.slice(5);
  return digits;
}

// How long the #client-texts deep link keeps following its section while the cards
// above it fill in (see the effect in PracticeDetailsPage), and the person's own
// scrolling that ends it early.
const TEXTS_SCROLL_SETTLE_MS = 5000;
const TEXTS_SCROLL_STOP_EVENTS = ['wheel', 'touchmove', 'keydown'];

function formatPhone(value) {
  const digits = value.replace(/\D/g, '').slice(0, 10);
  if (digits.length >= 7) return digits.slice(0, 3) + '-' + digits.slice(3, 6) + '-' + digits.slice(6);
  if (digits.length >= 4) return digits.slice(0, 3) + '-' + digits.slice(3);
  return digits;
}

function PracticeDetailsPage({ selectedPractice, onPracticeUpdated, readOnly, isAdmin, isSuperAdmin, canOnboard }) {
  // A #client-texts deep link (the Slack channel's Texts bookmark) opens on the tab
  // that holds the ContactsCard and scrolls to it once it has rendered.
  const [activeTab, setActiveTab] = useState(
    () => (window.location.hash === '#client-texts' ? 'integrations' : 'info'),
  );
  const scrolledToTexts = useRef(false);
  useEffect(() => {
    if (scrolledToTexts.current || window.location.hash !== '#client-texts') return undefined;
    const el = document.getElementById('client-texts');
    if (!el || typeof el.scrollIntoView !== 'function') return undefined;
    scrolledToTexts.current = true;
    el.scrollIntoView({ block: 'start' });
    // That first scroll lands while the cards above the section (the integrations
    // table, the sheet connector, the submission and SMS-number cards) are still
    // empty; each fills in from its own fetch, grows the panel and pushes the section
    // down, so the deep link ended up on whatever card finished last. Follow the
    // section while the panel is still growing: re-scroll on each resize of the tab
    // panel for a few seconds, and stop the moment the person scrolls themselves.
    if (typeof ResizeObserver === 'undefined') return undefined;
    const panel = el.closest('.info-tab') || el.parentElement;
    let done = false;
    const observer = new ResizeObserver(() => {
      if (!done) el.scrollIntoView({ block: 'start' });
    });
    const stop = () => {
      done = true;
      observer.disconnect();
      clearTimeout(timer);
      TEXTS_SCROLL_STOP_EVENTS.forEach((name) => window.removeEventListener(name, stop));
    };
    const timer = setTimeout(stop, TEXTS_SCROLL_SETTLE_MS);
    observer.observe(panel);
    TEXTS_SCROLL_STOP_EVENTS.forEach((name) => window.addEventListener(name, stop, { passive: true }));
    return stop;
  }, [activeTab, selectedPractice?.id]);
  // Setup wizard (docs/onboarding.md): its open items show as a banner until cleared.
  const [setupItems, setSetupItems] = useState([]);
  const [showSetup, setShowSetup] = useState(false);
  const [setupError, setSetupError] = useState(null);

  const loadSetupItems = useCallback(async () => {
    if (!canOnboard || readOnly || !selectedPractice?.id) { setSetupItems([]); return; }
    // Only a practice made in the last 30 days gets the banner (setup_service.RECENT_DAYS);
    // skip the request for the rest.
    const created = selectedPractice.created_at ? new Date(selectedPractice.created_at) : null;
    if (!created || Date.now() - created.getTime() > 30 * 24 * 3600 * 1000) { setSetupItems([]); return; }
    try {
      const response = await fetch(`${API_URL}/api/onboarding/practices/${selectedPractice.id}/setup`, { credentials: 'include' });
      const data = await response.json().catch(() => null);
      if (!response.ok) throw new Error((data && data.error) || 'Could not load setup status');
      // Only a new practice gets the banner; older ones are not nagged.
      setSetupItems(data.recent ? (data.open_items || []) : []);
      setSetupError(null);
    } catch (err) {
      setSetupError(err.message);
    }
  }, [canOnboard, readOnly, selectedPractice?.id, selectedPractice?.created_at]);

  useEffect(() => { loadSetupItems(); }, [loadSetupItems]);
  // The Integrations tab's sheet card reads the practice's resolved source from the
  // shared capabilities cache, the same one the sidebar reads.
  const capabilityGate = useCapabilities(selectedPractice?.id);
  // The Notifications tab's weekly report card; its one Save calls it (ref.current.save()).
  const weeklyReportsRef = useRef(null);

  // The client view (readOnly) has no Patients tab; never land on it either, whether
  // from a stale state or a role that changed under an open page.
  useEffect(() => {
    if (readOnly && !['info', 'providers', 'payers', 'billingSettings'].includes(activeTab)) setActiveTab('info');
  }, [readOnly, activeTab]);
  const [practice, setPractice] = useState(selectedPractice);
  const [patients, setPatients] = useState([]);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [patientSearch, setPatientSearch] = useState('');
  const [providerSearch, setProviderSearch] = useState('');
  const [payerSearchFilter, setPayerSearchFilter] = useState('');
  // NPI -> { taxonomy code: description }, from the NPI registry, for the Providers tab.
  const [taxonomyNames, setTaxonomyNames] = useState({});
  const taxonomyRequested = useRef(new Set());
  useEffect(() => {
    if (activeTab !== 'providers') return;
    const npis = (practice?.providers || [])
      .filter(p => p.npi && p.taxonomy_code && !taxonomyRequested.current.has(p.npi))
      .map(p => p.npi);
    [...new Set(npis)].forEach(async (npi) => {
      taxonomyRequested.current.add(npi);
      let names = {};
      try {
        const response = await fetch(`${API_URL}/api/npi-lookup/${npi}?practice_id=${practice.id}`, { credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          (data.taxonomies || []).forEach(t => { names[t.code] = t.desc; });
        }
      } catch {
        // Registry unreachable: the code shows without its name.
      }
      setTaxonomyNames(prev => ({ ...prev, [npi]: names }));
    });
  }, [activeTab, practice]);
  const [editingPatient, setEditingPatient] = useState(null);
  const [patientForm, setPatientForm] = useState({});
  const [savingPatient, setSavingPatient] = useState(false);
  const [patientError, setPatientError] = useState(null);
  // Guarantor sub-panel (collapsed by default, lazy-loaded on expand)
  const [guarantorOpen, setGuarantorOpen] = useState(false);
  const [guarantorLoading, setGuarantorLoading] = useState(false);
  const [guarantorForm, setGuarantorForm] = useState(null);
  const [guarantorSaving, setGuarantorSaving] = useState(false);
  const [guarantorMsg, setGuarantorMsg] = useState(null);
  const [guarantorPatientSearch, setGuarantorPatientSearch] = useState('');
  // Saved-state snapshot for the collapsed header summary (only updates on
  // load + successful save — never reflects in-progress edits).
  const [guarantorSummary, setGuarantorSummary] = useState(null);

  // Info form — initialized from prop so fields are never blank
  const [infoForm, setInfoForm] = useState(() => ({
    name: selectedPractice?.name || '',
    npi: selectedPractice?.npi || '',
    phone: selectedPractice?.phone || '',
    address1: selectedPractice?.address1 || '',
    address2: selectedPractice?.address2 || '',
    city: selectedPractice?.city || '',
    state: selectedPractice?.state || '',
    zip: selectedPractice?.zip || '',
    tax_id: selectedPractice?.tax_id || '',
    ptan: selectedPractice?.ptan || '',
    clearinghouse: selectedPractice?.clearinghouse || '',
  }));
  const [savingInfo, setSavingInfo] = useState(false);
  const [infoMessage, setInfoMessage] = useState(null);

  // Integration saves
  const [savingIntegration, setSavingIntegration] = useState(null); // which row is saving
  const [integrationMessage, setIntegrationMessage] = useState(null);

  // Contract — Atlas rate (% of collections)
  const [contractRate, setContractRate] = useState('');
  const [savingContract, setSavingContract] = useState(false);
  const [contractMessage, setContractMessage] = useState(null);
  const [syncingClearinghouse, setSyncingClearinghouse] = useState(false);
  const [syncingPracticebetter, setSyncingPracticebetter] = useState(false);

  // Credentials modal
  const [credentialsModal, setCredentialsModal] = useState(null); // { type: 'clearinghouse'|'emr'|'pm', system: string }
  const [credentialSchema, setCredentialSchema] = useState(null);
  const [credentialForm, setCredentialForm] = useState({});
  const [savingCredentials, setSavingCredentials] = useState(false);

  // Provider form
  const [showProviderForm, setShowProviderForm] = useState(false);
  const [editingProvider, setEditingProvider] = useState(null);
  const [providerForm, setProviderForm] = useState({
    first_name: '', last_name: '', npi: '', taxonomy_code: '', provider_type: 'PT', gender: '',
  });
  const [savingProvider, setSavingProvider] = useState(false);
  const [providerError, setProviderError] = useState(null);
  const [lookingUpNpi, setLookingUpNpi] = useState(false);
  const [npiLooked, setNpiLooked] = useState(false);

  const handleNpiLookup = async () => {
    const npi = providerForm.npi.replace(/\D/g, '');
    if (npi.length !== 10) {
      setProviderError('Enter a 10-digit NPI to look up');
      return;
    }
    setProviderError(null);
    setLookingUpNpi(true);
    try {
      const response = await fetch(`${API_URL}/api/npi-lookup/${npi}?practice_id=${selectedPractice.id}`, { credentials: 'include' });
      const data = await response.json();
      if (!response.ok) {
        setProviderError(data.error || 'NPI lookup failed');
        return;
      }
      if (data.type === 'organization') {
        if (!data.allowed) {
          setProviderError(data.refusal || 'This is an organization NPI (Type 2). Enter an individual provider NPI.');
          return;
        }
        // The agency itself: its name is the row's name, there is no first name or gender.
        setProviderForm({
          ...providerForm,
          npi,
          first_name: '',
          last_name: data.name || providerForm.last_name,
          taxonomy_code: data.taxonomy_code || providerForm.taxonomy_code,
          provider_type: ORGANIZATION_PROVIDER_TYPE,
          gender: '',
        });
        setNpiLooked(true);
        return;
      }
      setProviderForm({
        ...providerForm,
        npi,
        first_name: data.first_name || providerForm.first_name,
        last_name: data.last_name || providerForm.last_name,
        taxonomy_code: data.taxonomy_code || providerForm.taxonomy_code,
        provider_type: data.provider_type || providerForm.provider_type,
        gender: data.gender || providerForm.gender,
      });
      setNpiLooked(true);
    } catch (err) {
      setProviderError('Failed to reach NPI registry');
    } finally {
      setLookingUpNpi(false);
    }
  };

  // Payer form
  const [showPayerForm, setShowPayerForm] = useState(false);
  const [payerSearch, setPayerSearch] = useState('');
  const [payerResults, setPayerResults] = useState([]);
  const [searchingPayers, setSearchingPayers] = useState(false);
  const [selectedStediPayer, setSelectedStediPayer] = useState(null);
  const [isCustomPayer, setIsCustomPayer] = useState(false);
  const [customPayerName, setCustomPayerName] = useState('');
  const [customPayerId, setCustomPayerId] = useState('');
  const [savingPayer, setSavingPayer] = useState(false);
  const [payerError, setPayerError] = useState(null);
  const [payerSearchTimeout, setPayerSearchTimeout] = useState(null);
  const [editingPayerId, setEditingPayerId] = useState(null);
  const [payerIdError, setPayerIdError] = useState(null); // inline Payer ID save failure
  // Payer knowledge-base detail fields (auth, enrollment, portal, contacts, docs)
  const emptyPayerDetails = {
    in_network: true,
    is_active: true,
    requires_auth: '', // '' unknown | 'yes' | 'no'
    auth_submission_method: '', // '' | 'phone' | 'fax' | 'portal'
    auth_detail: '',
    portal_username: '',
    portal_password: '',
    portal_password_set: false,
    portal_url: '',
    phone: '',
    fax: '',
    era_setup: false,
    eft_setup: false,
    era_portal: '',
    payer_node_id: null, // link into the rules engine's payer tree
    payer_node: null,
  };
  const [payerDetails, setPayerDetails] = useState(emptyPayerDetails);
  const [expandedPayerId, setExpandedPayerId] = useState(null);
  const [revealedPw, setRevealedPw] = useState({});
  const setPD = (key, val) => setPayerDetails((d) => ({ ...d, [key]: val }));

  const handlePayerSearch = (query) => {
    setPayerSearch(query);
    setSelectedStediPayer(null);
    if (payerSearchTimeout) clearTimeout(payerSearchTimeout);
    if (query.length < 2) { setPayerResults([]); return; }
    const timeout = setTimeout(async () => {
      setSearchingPayers(true);
      try {
        const response = await fetch(`${API_URL}/api/stedi/payers/search?query=${encodeURIComponent(query)}`, { credentials: 'include' });
        const data = await response.json();
        setPayerResults(data.payers || []);
      } catch (err) {
        setPayerResults([]);
      } finally {
        setSearchingPayers(false);
      }
    }, 300);
    setPayerSearchTimeout(timeout);
  };

  const handleSelectStediPayer = (payer) => {
    setSelectedStediPayer(payer);
    setPayerSearch(payer.name);
    setPayerResults([]);
  };

  const openEditPayer = (p) => {
    setEditingPayerId(p.id);
    setShowPayerForm(true);
    setPayerError(null);
    setPayerSearch('');
    setPayerResults([]);
    setPayerDetails({
      in_network: p.in_network !== false,
      is_active: p.is_active !== false,
      requires_auth: p.requires_auth === true ? 'yes' : p.requires_auth === false ? 'no' : '',
      auth_submission_method: p.auth_submission_method || '',
      auth_detail: p.auth_detail || '',
      portal_username: p.portal_username || '',
      portal_password: '', // blank leaves it unchanged; retype to replace
      portal_password_set: !!p.portal_password_set,
      portal_url: p.portal_url || '',
      phone: p.phone || '',
      fax: p.fax || '',
      era_setup: !!p.era_setup,
      eft_setup: !!p.eft_setup,
      era_portal: p.era_portal || '',
      payer_node_id: p.payer_node_id ?? null,
      payer_node: p.payer_node || null,
    });
    if (p.is_custom) {
      setIsCustomPayer(true);
      setCustomPayerName(p.payer_name || '');
      setCustomPayerId(p.payer_id || '');
      setSelectedStediPayer(null);
    } else {
      setIsCustomPayer(false);
      setSelectedStediPayer({ name: p.payer_name, payer_id: p.payer_id });
      setCustomPayerName('');
      setCustomPayerId('');
    }
  };

  const closePayerForm = () => {
    setShowPayerForm(false);
    setEditingPayerId(null);
    setPayerSearch('');
    setSelectedStediPayer(null);
    setIsCustomPayer(false);
    setCustomPayerName('');
    setCustomPayerId('');
    setPayerDetails(emptyPayerDetails);
  };

  const handleSavePayer = async () => {
    setPayerError(null);
    const isEdit = editingPayerId != null;
    let payerName, payerId;
    if (!isEdit) {
      payerName = isCustomPayer ? customPayerName.trim() : selectedStediPayer?.name;
      payerId = isCustomPayer ? customPayerId.trim() : selectedStediPayer?.payer_id;
      if (!payerName || !payerId) {
        setPayerError(isCustomPayer ? 'Payer name and ID are required' : 'Select a payer from the search results');
        return;
      }
    }
    setSavingPayer(true);
    try {
      const url = isEdit
        ? `${API_URL}/api/practices/${selectedPractice.id}/payers/${editingPayerId}`
        : `${API_URL}/api/practices/${selectedPractice.id}/payers`;
      const d = payerDetails;
      const body = {
        in_network: d.in_network !== false,
        is_active: d.is_active !== false,
        requires_auth: d.requires_auth === 'yes' ? true : d.requires_auth === 'no' ? false : null,
        auth_submission_method: d.auth_submission_method || null,
        auth_detail: d.auth_detail || null,
        era_setup: !!d.era_setup,
        eft_setup: !!d.eft_setup,
        era_portal: d.era_portal || null,
        portal_url: d.portal_url || null,
        portal_username: d.portal_username || null,
        phone: d.phone || null,
        fax: d.fax || null,
        payer_node_id: d.payer_node_id ?? null,
      };
      // Identity is only set when creating; editing keeps the existing payer.
      if (!isEdit) {
        body.payer_name = payerName;
        body.payer_id = payerId;
        body.is_custom = isCustomPayer;
      }
      // Only send the password when the user typed one; a blank field leaves it unchanged.
      if (d.portal_password) body.portal_password = d.portal_password;
      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      if (response.ok) {
        closePayerForm();
        loadPractice();
        if (onPracticeUpdated) onPracticeUpdated();
      } else {
        const data = await response.json();
        setPayerError(data.error || (isEdit ? 'Failed to update payer' : 'Failed to add payer'));
      }
    } catch (err) {
      setPayerError(err.message);
    } finally {
      setSavingPayer(false);
    }
  };

  const handleDeleteFromEdit = async () => {
    const payerId = editingPayerId;
    if (payerId == null) return;
    if (!window.confirm('Remove this payer? This cannot be undone.')) return;
    try {
      const response = await fetch(`${API_URL}/api/practices/${selectedPractice.id}/payers/${payerId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (response.ok) {
        closePayerForm();
        loadPractice();
        if (onPracticeUpdated) onPracticeUpdated();
      } else {
        alert('Failed to remove payer');
      }
    } catch (err) {
      alert('Failed to remove payer: ' + err.message);
    }
  };

  const revealPortalPassword = async (payerId) => {
    try {
      const response = await fetch(
        `${API_URL}/api/practices/${selectedPractice.id}/payers/${payerId}/portal-password`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        setRevealedPw((m) => ({ ...m, [payerId]: data.portal_password }));
      } else {
        alert('Could not reveal password');
      }
    } catch (err) {
      alert('Could not reveal password: ' + err.message);
    }
  };

  const hidePortalPassword = (payerId) => {
    setRevealedPw((m) => {
      const next = { ...m };
      delete next[payerId];
      return next;
    });
  };

  // Patient upload modal
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState(null);
  const [uploadFormat, setUploadFormat] = useState('');
  const [rememberFormat, setRememberFormat] = useState(false);

  // Payer resolution modal
  const [showResolveModal, setShowResolveModal] = useState(false);
  const [resolveItems, setResolveItems] = useState([]);
  const [resolving, setResolving] = useState(false);

  // Fee schedule

  // Daily copay email config
  const [copayConfigForm, setCopayConfigForm] = useState({
    copay_email_recipients: '',
    copay_email_enabled: false,
    copay_email_lookahead_days: 1,
    copay_email_schedule_days: [1, 2, 3, 4, 5, 6, 7],
  });
  const [savingCopayConfig, setSavingCopayConfig] = useState(false);
  // The practice whose daily email settings the form holds. Until they have loaded for
  // the selected practice, Save leaves the daily email alone: the form's defaults (no
  // recipients, off), or the last practice's settings, must never be written over it.
  const [copayLoadedFor, setCopayLoadedFor] = useState(null);
  const [copayConfigMessage, setCopayConfigMessage] = useState(null);
  const [previewingCopayEmail, setPreviewingCopayEmail] = useState(false);
  const [copaySampleMessage, setCopaySampleMessage] = useState(null);

  // Load practice details
  const loadPractice = useCallback(async () => {
    if (!selectedPractice) return;
    try {
      const response = await fetch(`${API_URL}/api/practices/${selectedPractice.id}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setPractice(data);
        setInfoForm({
          name: data.name || '',
          npi: data.npi || '',
          phone: data.phone || '',
          address1: data.address1 || '',
          address2: data.address2 || '',
          city: data.city || '',
          state: data.state || '',
          zip: data.zip || '',
          tax_id: data.tax_id || '',
          ptan: data.ptan || '',
          clearinghouse: data.clearinghouse || '',
        });
        setCopayConfigForm({
          copay_email_recipients: Array.isArray(data.copay_email_recipients)
            ? data.copay_email_recipients.join(', ')
            : (data.copay_email_recipients || ''),
          copay_email_enabled: !!data.copay_email_enabled,
          copay_email_lookahead_days: data.copay_email_lookahead_days ?? 1,
          copay_email_schedule_days: Array.isArray(data.copay_email_schedule_days)
            ? data.copay_email_schedule_days
            : [1, 2, 3, 4, 5, 6, 7],
        });
        setCopayLoadedFor(data.id);
        setContractRate(
          data.collections_fee_pct !== null && data.collections_fee_pct !== undefined
            ? String(data.collections_fee_pct)
            : ''
        );
      }
    } catch (err) {
      // Silently fail
    }
  }, [selectedPractice]);

  // The daily email's own settings (copay-config); throws with the server's message.
  const saveDailyEmail = async () => {
    if (copayLoadedFor !== selectedPractice.id) return;
    const recipients = copayConfigForm.copay_email_recipients
      .split(/[,\n]/)
      .map((r) => r.trim())
      .filter(Boolean);
    const response = await fetch(
      `${API_URL}/api/practices/${selectedPractice.id}/copay-config`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          copay_email_recipients: recipients,
          copay_email_enabled: !!copayConfigForm.copay_email_enabled,
          copay_email_lookahead_days:
            copayConfigForm.copay_email_lookahead_days === ''
              ? 1
              : Math.max(0, Math.min(14, Number(copayConfigForm.copay_email_lookahead_days) || 0)),
          copay_email_schedule_days: Array.isArray(
            copayConfigForm.copay_email_schedule_days
          )
            ? copayConfigForm.copay_email_schedule_days
                .filter((d) => d >= 1 && d <= 7)
                .sort((a, b) => a - b)
            : [1, 2, 3, 4, 5, 6, 7],
        }),
      }
    );
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.error || 'Failed to save');
    loadPractice();
  };

  // The Notifications tab's one Save: every part is saved even when another fails,
  // and the message names each part that did not save.
  const handleSaveNotifications = async () => {
    if (!selectedPractice) return;
    setCopayConfigMessage(null);
    setSavingCopayConfig(true);
    const parts = [
      ['Daily email', saveDailyEmail],
      ['Weekly report', () => weeklyReportsRef.current && weeklyReportsRef.current.save()],
    ];
    const failed = [];
    for (const [name, save] of parts) {
      try {
        await save();
      } catch (err) {
        failed.push(`${name}: ${err.message}`);
      }
    }
    setCopayConfigMessage(
      failed.length
        ? { type: 'error', text: `Not saved — ${failed.join('; ')}` }
        : { type: 'success', text: 'Settings saved.' }
    );
    setSavingCopayConfig(false);
  };

  const handlePreviewCopayEmail = async () => {
    if (!selectedPractice) return;
    setCopaySampleMessage(null);
    setPreviewingCopayEmail(true);
    try {
      // To the signed-in user, whatever the saved recipient list and the
      // Enabled? switch say: a sample is an explicit ask (David, 2026-10-28).
      const response = await fetch(
        `${API_URL}/api/practices/${selectedPractice.id}/copay-email/preview`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ to: 'me' }),
        }
      );
      let data = {};
      try {
        data = await response.json();
      } catch (parseErr) {
        data = {};
      }
      if (!response.ok) {
        const reasons = Object.entries(data.failures || {})
          .map(([addr, why]) => `${addr}: ${why}`)
          .join('; ');
        setCopaySampleMessage({
          type: 'error',
          text: `Sample not sent${reasons ? ` — ${reasons}` : data.error ? ` — ${data.error}` : ` (HTTP ${response.status})`}`,
        });
      } else {
        const ids = data.message_ids || {};
        const sent = (data.sent_to || [])
          .map((addr) => (ids[addr] ? `${addr} (Postmark id ${ids[addr]})` : addr))
          .join(', ');
        setCopaySampleMessage({
          type: 'success',
          text: `Sample sent to ${sent || '(nobody)'}.`,
        });
      }
    } catch (err) {
      setCopaySampleMessage({
        type: 'error',
        text: 'Sample not sent — ' + err.message,
      });
    } finally {
      setPreviewingCopayEmail(false);
    }
  };

  const loadPatients = useCallback(async () => {
    if (!selectedPractice) return;
    setLoadingPatients(true);
    try {
      const response = await fetch(`${API_URL}/api/practices/${selectedPractice.id}/patients`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setPatients(data.patients || []);
      }
    } catch (err) {
      // Silently fail
    } finally {
      setLoadingPatients(false);
    }
  }, [selectedPractice]);

  useEffect(() => {
    loadPractice();
  }, [loadPractice]);

  useEffect(() => {
    if (activeTab === 'patients') loadPatients();
  }, [activeTab, loadPatients]);

  // Save practice info
  const handleSaveInfo = async (e) => {
    e.preventDefault();
    if (readOnly) return;
    setInfoMessage(null);

    // Client-side validation
    const npiErr = validateNpi(infoForm.npi);
    if (npiErr) { setInfoMessage({ type: 'error', text: npiErr }); return; }
    const taxErr = validateTaxId(infoForm.tax_id);
    if (taxErr) { setInfoMessage({ type: 'error', text: taxErr }); return; }
    const zipErr = validateZip(infoForm.zip);
    if (zipErr) { setInfoMessage({ type: 'error', text: zipErr }); return; }

    setSavingInfo(true);
    try {
      const response = await fetch(`${API_URL}/api/practices/${selectedPractice.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(infoForm),
      });
      if (response.ok) {
        const data = await response.json();
        setPractice(data);
        setInfoMessage({ type: 'success', text: 'Practice details saved.' });
        // The EMR / PM / clearinghouse dropdowns in this form are exactly what the
        // connector registry reads, so a save can change which features this practice
        // has. Re-read the gate rather than leaving the panel below and the sidebar
        // describing the practice as it was before the save.
        invalidateCapabilities(selectedPractice.id);
        // The card-on-file consent wording quotes the practice NAME, and the
        // Collections card caches it. A rename must not leave a consent surface
        // quoting the old one.
        invalidateCollectionsSettings(selectedPractice.id);
        if (onPracticeUpdated) onPracticeUpdated();
      } else {
        const data = await response.json();
        setInfoMessage({ type: 'error', text: data.error || 'Failed to save' });
      }
    } catch (err) {
      setInfoMessage({ type: 'error', text: err.message });
    } finally {
      setSavingInfo(false);
    }
  };

  const openCredentialsModal = async (type, system) => {
    setCredentialForm({});
    setCredentialSchema(null);
    setCredentialsModal({ type, system });
    try {
      const practiceQuery = selectedPractice?.id ? `?practice_id=${selectedPractice.id}` : '';
      const response = await fetch(`${API_URL}/api/credential-schema/${system}${practiceQuery}`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setCredentialSchema(data.fields);
      }
    } catch {
      // Schema fetch failed — modal will show loading state
    }
  };

  const runClearinghouseSync = async () => {
    if (!selectedPractice?.id) return;
    setSyncingClearinghouse(true);
    setIntegrationMessage({ type: 'info', text: 'Sync running…' });
    try {
      const response = await fetch(
        `${API_URL}/api/practices/${selectedPractice.id}/clearinghouse/sync`,
        { method: 'POST', credentials: 'include' },
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setIntegrationMessage({
          type: 'error',
          text: data.error || `Sync failed (${response.status})`,
        });
        return;
      }
      const errs = data.errors || [];
      if (errs.length) {
        setIntegrationMessage({ type: 'error', text: `Sync errors: ${errs.join('; ')}` });
        return;
      }
      const parts = [];
      if (data.claims_updated != null) parts.push(`${data.claims_updated} claims updated`);
      if (data.claims_created) parts.push(`${data.claims_created} new`);
      if (data.eras_created != null) parts.push(`${data.eras_created} ERAs`);
      if (data.claims_posted) parts.push(`${data.claims_posted} posted`);
      setIntegrationMessage({
        type: 'success',
        text: parts.length ? `Sync complete — ${parts.join(', ')}.` : 'Sync complete.',
      });
    } catch {
      setIntegrationMessage({ type: 'error', text: 'Sync failed (network error).' });
    } finally {
      setSyncingClearinghouse(false);
    }
  };

  const runPracticebetterSync = async () => {
    if (!selectedPractice?.id) return;
    setSyncingPracticebetter(true);
    setIntegrationMessage({ type: 'info', text: 'PracticeBetter sync running…' });
    try {
      const response = await fetch(
        `${API_URL}/api/practices/${selectedPractice.id}/practicebetter/sync`,
        { method: 'POST', credentials: 'include' },
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setIntegrationMessage({
          type: 'error',
          text: data.error || `Sync failed (${response.status})`,
        });
        return;
      }
      const claims = data.claims || {};
      const patients = data.patients || {};
      const parts = [];
      if (claims.created != null) parts.push(`${claims.created} new claim${claims.created === 1 ? '' : 's'}`);
      if (claims.updated) parts.push(`${claims.updated} updated`);
      if (patients.created != null) parts.push(`${patients.created} new patient${patients.created === 1 ? '' : 's'}`);
      if (patients.updated) parts.push(`${patients.updated} patient updates`);
      const modeNote = data.mode === 'backfill' ? ' (initial backfill)' : '';
      setIntegrationMessage({
        type: 'success',
        text: parts.length
          ? `PracticeBetter sync complete${modeNote} — ${parts.join(', ')}.`
          : `PracticeBetter sync complete${modeNote}.`,
      });
      // Refresh practice (for patient_count tab badge) and the patient list.
      // Errors are non-fatal — the sync already succeeded.
      loadPractice().catch(() => {});
      loadPatients().catch(() => {});
    } catch {
      setIntegrationMessage({ type: 'error', text: 'PracticeBetter sync failed (network error).' });
    } finally {
      setSyncingPracticebetter(false);
    }
  };

  const handleSaveCredentials = async () => {
    if (!credentialsModal) return;
    setSavingCredentials(true);
    const payloadKey = credentialsModal.type === 'clearinghouse' ? 'clearinghouse_credentials'
      : credentialsModal.type === 'emr' ? 'emr_credentials' : 'pm_credentials';
    try {
      const response = await fetch(`${API_URL}/api/practices/${selectedPractice.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ [payloadKey]: credentialForm }),
      });
      if (response.ok) {
        const data = await response.json();
        setPractice(data);
        const wasClearinghouse = credentialsModal.type === 'clearinghouse'
          && ['claimmd', 'stedi'].includes(data.clearinghouse);
        setCredentialsModal(null);
        setIntegrationMessage({ type: 'success', text: 'Credentials saved.' });
        // Stored credentials are half of what makes a source real (see
        // connector_registry: an eCW dropdown with no credentials is not a feed), so
        // saving them can turn features on.
        invalidateCapabilities(selectedPractice.id);
        if (wasClearinghouse) {
          // Auto-trigger sync so the user sees a result without an extra click.
          runClearinghouseSync();
        }
      } else {
        const data = await response.json().catch(() => ({}));
        setIntegrationMessage({ type: 'error', text: data.error || 'Failed to save credentials' });
        // A 403 (e.g. a non-admin trying to edit credentials) can't be fixed by
        // retrying in the dialog, so close it to reveal the message banner. Other
        // failures keep the modal open so the user can correct and retry without
        // losing what they typed.
        if (response.status === 403) {
          setCredentialsModal(null);
        }
      }
    } catch {
      setIntegrationMessage({ type: 'error', text: 'Failed to save credentials' });
    } finally {
      setSavingCredentials(false);
    }
  };

  // Add / edit provider
  const handleSaveProvider = async (e) => {
    e.preventDefault();
    setProviderError(null);

    const isOrganization = providerForm.provider_type === ORGANIZATION_PROVIDER_TYPE;
    if (isOrganization ? !providerForm.last_name.trim() : (!providerForm.first_name.trim() || !providerForm.last_name.trim())) {
      setProviderError(isOrganization ? 'The organization name is required' : 'First and last name are required'); return;
    }
    const npiErr = validateNpi(providerForm.npi);
    if (npiErr) { setProviderError(npiErr); return; }
    if (providerForm.taxonomy_code && !/^[0-9A-Z]{10}$/.test(providerForm.taxonomy_code.replace(/\s/g, ''))) {
      setProviderError('Taxonomy code must be exactly 10 alphanumeric characters (e.g., 363L00000X)'); return;
    }

    setSavingProvider(true);
    try {
      const url = editingProvider
        ? `${API_URL}/api/practices/${selectedPractice.id}/providers/${editingProvider.id}`
        : `${API_URL}/api/practices/${selectedPractice.id}/providers`;
      const method = editingProvider ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(providerForm),
      });
      if (response.ok) {
        setShowProviderForm(false);
        setEditingProvider(null);
        setProviderForm({ first_name: '', last_name: '', npi: '', taxonomy_code: '', provider_type: 'PT', gender: '' });
        loadPractice();
        if (onPracticeUpdated) onPracticeUpdated();
      }
    } catch (err) {
      // Silently fail
    } finally {
      setSavingProvider(false);
    }
  };

  const handleEditProvider = (provider) => {
    setEditingProvider(provider);
    setProviderForm({
      first_name: provider.first_name || '',
      last_name: provider.last_name || '',
      npi: provider.npi || '',
      taxonomy_code: provider.taxonomy_code || '',
      provider_type: provider.provider_type || 'PT',
      gender: provider.gender || '',
    });
    setShowProviderForm(true);
  };

  const handleDeleteProvider = async (providerId) => {
    if (!window.confirm('Remove this provider?')) return;
    try {
      const response = await fetch(`${API_URL}/api/practices/${selectedPractice.id}/providers/${providerId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (response.ok) {
        loadPractice();
        if (onPracticeUpdated) onPracticeUpdated();
      } else {
        alert('Failed to remove provider');
      }
    } catch (err) {
      alert('Failed to remove provider: ' + err.message);
    }
  };

  // Upload patients CSV
  const handleUploadPatients = async () => {
    if (!uploadFile) return;
    setUploading(true);
    setUploadMessage(null);
    const formData = new FormData();
    formData.append('file', uploadFile);
    if (uploadFormat) formData.append('format', uploadFormat);
    if (rememberFormat) formData.append('remember', 'true');

    try {
      const response = await fetch(`${API_URL}/api/practices/${selectedPractice.id}/patients/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      const data = await response.json();
      if (response.ok) {
        const fmtLabel = { practicesuite: 'PracticeSuite', native: 'Atlas' }[data.format_detected] || data.format_detected;
        let msg = `Uploaded ${data.patients_loaded} patients (${fmtLabel} format).`;
        let msgType = 'success';
        if (data.unmatched_payers && data.unmatched_payers.length > 0) {
          msgType = 'warning';
          msg += ` ${data.unmatched_count} payer(s) need mapping.`;
          // Pre-populate resolve items with suggestions and default to the suggestion
          const items = data.unmatched_payers.map(u => ({
            csv_name: u.csv_name,
            suggested_payer_db_id: u.suggested_payer_db_id,
            suggested_payer_name: u.suggested_payer_name,
            selected_payer_db_id: u.suggested_payer_db_id || '',
            score: u.score,
          }));
          setResolveItems(items);
          setShowResolveModal(true);
        }
        setUploadMessage({ type: msgType, text: msg });
        setUploadFile(null);
        setShowUploadModal(false);
        loadPatients();
        if (onPracticeUpdated) onPracticeUpdated();
      } else {
        setUploadMessage({ type: 'error', text: data.error || 'Upload failed' });
      }
    } catch (err) {
      setUploadMessage({ type: 'error', text: err.message });
    } finally {
      setUploading(false);
    }
  };

  const handleResolvePayers = async () => {
    setResolving(true);
    try {
      // First, create any new payers
      const newPayerItems = resolveItems.filter(i => i.selected_payer_db_id === 'new' && i.new_payer_name && i.new_payer_id);
      for (const item of newPayerItems) {
        const resp = await fetch(`${API_URL}/api/practices/${selectedPractice.id}/payers`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ payer_name: item.new_payer_name, payer_id: item.new_payer_id, is_custom: true }),
        });
        if (resp.ok) {
          const created = await resp.json();
          // Update the item to reference the newly created payer's DB id
          item.selected_payer_db_id = created.id;
        }
      }

      // Now resolve all mappings (existing + newly created)
      const mappings = resolveItems
        .filter(item => item.selected_payer_db_id && item.selected_payer_db_id !== 'new')
        .map(item => ({ csv_name: item.csv_name, payer_db_id: item.selected_payer_db_id }));

      if (mappings.length > 0) {
        await fetch(`${API_URL}/api/practices/${selectedPractice.id}/patients/resolve-payers`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ mappings }),
        });
      }

      const total = mappings.length;
      setUploadMessage({ type: 'success', text: `Resolved ${total} payer mapping(s). Aliases saved for future uploads.` });
      setShowResolveModal(false);
      loadPatients();
      loadPractice();
      if (onPracticeUpdated) onPracticeUpdated();
    } catch (err) {
      setUploadMessage({ type: 'error', text: err.message });
    } finally {
      setResolving(false);
    }
  };

  // Patient edit/delete handlers
  const handleEditPatient = async (patient) => {
    // Fetch full patient details (includes cards on file)
    let fullPatient = patient;
    if (patient.id && !patient._isNew) {
      try {
        const resp = await fetch(
          `${API_URL}/api/practices/${selectedPractice.id}/patients/${patient.id}`,
          { credentials: 'include' }
        );
        if (resp.ok) {
          fullPatient = await resp.json();
        }
      } catch (err) {
        console.warn('Failed to fetch full patient details', err);
      }
    }
    const configuredPayer = (practice?.payers || []).find((payer) => (
      payer.payer_id === (fullPatient.payer_id || '')
    ));
    setEditingPatient(fullPatient);
    setPatientForm({
      subscriber_id: fullPatient.subscriber_id || '',
      subscriber_first_name: fullPatient.subscriber_first_name || '',
      subscriber_last_name: fullPatient.subscriber_last_name || '',
      subscriber_dob: fullPatient.subscriber_dob || '',
      subscriber_gender: fullPatient.subscriber_gender || '',
      payer_id: configuredPayer?.payer_id || '',
      payer_name: configuredPayer?.payer_name || '',
      patient_is_subscriber: fullPatient.patient_is_subscriber || 'Y',
      patient_first_name: fullPatient.patient_first_name || '',
      patient_last_name: fullPatient.patient_last_name || '',
      patient_dob: fullPatient.patient_dob || '',
      patient_gender: fullPatient.patient_gender || '',
      patient_relationship_code: fullPatient.patient_relationship_code || '',
      phone: fullPatient.phone || '',
      email: fullPatient.email || '',
      address1: fullPatient.address1 || '',
      address2: fullPatient.address2 || '',
      city: fullPatient.city || '',
      state: fullPatient.state || '',
      zip: fullPatient.zip || '',
      payment_type: fullPatient.payment_type || 'unknown',
      secondary_payer_id: fullPatient.secondary_payer_id || '',
      secondary_payer_name: fullPatient.secondary_payer_name || '',
      secondary_subscriber_id: fullPatient.secondary_subscriber_id || '',
      tertiary_payer_id: fullPatient.tertiary_payer_id || '',
      tertiary_payer_name: fullPatient.tertiary_payer_name || '',
      tertiary_subscriber_id: fullPatient.tertiary_subscriber_id || '',
      estimated_copay: fullPatient.estimated_copay != null ? String(fullPatient.estimated_copay) : '',
    });
    setPatientError(null);
    // Reset the (collapsed) guarantor panel for the newly opened patient.
    setGuarantorOpen(false);
    setGuarantorForm(null);
    setGuarantorMsg(null);
    setGuarantorPatientSearch('');
    setGuarantorSummary(null);
  };

  // Lazy-load the guarantor only when the panel is first expanded.
  const toggleGuarantorPanel = async () => {
    if (guarantorOpen) {
      setGuarantorOpen(false);
      return;
    }
    setGuarantorOpen(true);
    if (guarantorForm) return; // already loaded
    setGuarantorLoading(true);
    setGuarantorMsg(null);
    try {
      const resp = await fetch(
        `${API_URL}/api/practices/${selectedPractice.id}/patients/${editingPatient.id}/guarantor`,
        { credentials: 'include' }
      );
      if (!resp.ok) throw new Error('Failed to load guarantor');
      const g = (await resp.json()).guarantor;
      const mode = g?.source_patient_id ? 'link' : (g?.relationship_to_patient || 'self') === 'self' ? 'self' : 'manual';
      setGuarantorForm({
        relationship_to_patient: g?.relationship_to_patient || 'self',
        mode,
        source_patient_id: g?.source_patient_id || null,
        source_patient_name: g?.source_patient_id ? `${g.first_name || ''} ${g.last_name || ''}`.trim() : '',
        first_name: g?.first_name || '',
        last_name: g?.last_name || '',
        dob: g?.dob || '',
        address1: g?.address1 || '',
        address2: g?.address2 || '',
        city: g?.city || '',
        state: g?.state || '',
        zip: g?.zip || '',
        phone: g?.phone || '',
        email: g?.email || '',
        notes: g?.notes || '',
      });
      setGuarantorSummary({
        relationship_to_patient: g?.relationship_to_patient || 'self',
        first_name: g?.first_name || '',
        last_name: g?.last_name || '',
      });
    } catch (err) {
      setGuarantorMsg({ type: 'error', text: err.message });
    } finally {
      setGuarantorLoading(false);
    }
  };

  const updateGuarantorField = (field, value) => {
    setGuarantorForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveGuarantor = async () => {
    if (readOnly || !guarantorForm) return;
    setGuarantorSaving(true);
    setGuarantorMsg(null);
    const { mode, relationship_to_patient } = guarantorForm;
    let payload;
    if (mode === 'self' || (mode === 'link' && !guarantorForm.source_patient_id)) {
      // "Self", or link mode with no patient linked: no separate guarantor —
      // revert to the patient being their own guarantor (i.e. remove it).
      payload = { relationship_to_patient: 'self' };
    } else if (mode === 'link') {
      payload = {
        relationship_to_patient: relationship_to_patient === 'self' ? 'other' : relationship_to_patient,
        source_patient_id: guarantorForm.source_patient_id,
      };
    } else {
      payload = {
        relationship_to_patient: relationship_to_patient === 'self' ? 'other' : relationship_to_patient,
        first_name: guarantorForm.first_name,
        last_name: guarantorForm.last_name,
        dob: guarantorForm.dob,
        address1: guarantorForm.address1,
        address2: guarantorForm.address2,
        city: guarantorForm.city,
        state: guarantorForm.state,
        zip: guarantorForm.zip,
        phone: guarantorForm.phone,
        email: guarantorForm.email,
      };
    }
    try {
      const resp = await fetch(
        `${API_URL}/api/practices/${selectedPractice.id}/patients/${editingPatient.id}/guarantor`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify(payload),
        }
      );
      const body = await resp.json();
      if (!resp.ok) throw new Error(body.error || 'Failed to save guarantor');
      const g = body.guarantor;
      const newMode = g?.source_patient_id ? 'link' : (g?.relationship_to_patient || 'self') === 'self' ? 'self' : 'manual';
      setGuarantorForm({
        relationship_to_patient: g?.relationship_to_patient || 'self',
        mode: newMode,
        source_patient_id: g?.source_patient_id || null,
        source_patient_name: g?.source_patient_id ? `${g.first_name || ''} ${g.last_name || ''}`.trim() : '',
        first_name: g?.first_name || '',
        last_name: g?.last_name || '',
        dob: g?.dob || '',
        address1: g?.address1 || '',
        address2: g?.address2 || '',
        city: g?.city || '',
        state: g?.state || '',
        zip: g?.zip || '',
        phone: g?.phone || '',
        email: g?.email || '',
        notes: g?.notes || '',
      });
      setGuarantorSummary({
        relationship_to_patient: g?.relationship_to_patient || 'self',
        first_name: g?.first_name || '',
        last_name: g?.last_name || '',
      });
      setGuarantorMsg({
        type: 'success',
        text: (g?.relationship_to_patient || 'self') === 'self'
          ? 'Guarantor removed — patient is their own guarantor.'
          : 'Guarantor saved.',
      });
    } catch (err) {
      setGuarantorMsg({ type: 'error', text: err.message });
    } finally {
      setGuarantorSaving(false);
    }
  };

  const handleSavePatient = async (e) => {
    e.preventDefault();
    if (readOnly) return;
    setPatientError(null);
    if (!patientForm.subscriber_first_name.trim() || !patientForm.subscriber_last_name.trim()) {
      setPatientError('Subscriber first and last name are required');
      return;
    }
    const configuredPayer = (practice?.payers || []).find((payer) => (
      payer.payer_id === patientForm.payer_id
    ));
    const payload = {
      ...patientForm,
      payer_id: configuredPayer?.payer_id || '',
      payer_name: configuredPayer?.payer_name || '',
    };
    setSavingPatient(true);
    const isNew = editingPatient?._isNew;
    const url = isNew
      ? `${API_URL}/api/practices/${selectedPractice.id}/patients`
      : `${API_URL}/api/practices/${selectedPractice.id}/patients/${editingPatient.id}`;
    try {
      const response = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        // If the copay field changed, also persist via the dedicated endpoint.
        // Only fires for existing patients with a primary insurance.
        const savedPatient = await response.json();
        const newCopayRaw = (patientForm.estimated_copay || '').trim();
        const oldCopay = editingPatient?.estimated_copay;
        const oldCopayStr = oldCopay != null ? String(oldCopay) : '';
        let copayFailed = false;
        if (!isNew && (savedPatient.payer_id || '').trim() && newCopayRaw !== oldCopayStr) {
          const copayBody = newCopayRaw === '' ? { amount: null } : { amount: Number(newCopayRaw) };
          try {
            const copayResp = await fetch(
              `${API_URL}/api/practices/${selectedPractice.id}/patients/${editingPatient.id}/copay`,
              {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(copayBody),
              }
            );
            if (!copayResp.ok) {
              copayFailed = true;
              const data = await copayResp.json().catch(() => ({}));
              setPatientError(
                `Patient saved, but copay update failed: ${data.error || 'server error'}. Reopen to retry.`
              );
            }
          } catch (err) {
            copayFailed = true;
            setPatientError(`Patient saved, but copay update failed: ${err.message}. Reopen to retry.`);
          }
        }
        if (!copayFailed) {
          setEditingPatient(null);
        }
        loadPatients();
      } else {
        const data = await response.json();
        setPatientError(data.error || 'Failed to save');
      }
    } catch (err) {
      setPatientError(err.message);
    } finally {
      setSavingPatient(false);
    }
  };

  const handleDeletePatient = async (patient) => {
    if (!window.confirm(`Delete patient ${patient.display_name}?`)) return;
    try {
      const response = await fetch(`${API_URL}/api/practices/${selectedPractice.id}/patients/${patient.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (response.ok) {
        loadPatients();
        if (onPracticeUpdated) onPracticeUpdated();
      } else {
        alert('Failed to delete patient');
      }
    } catch (err) {
      alert('Failed to delete patient: ' + err.message);
    }
  };

  if (!selectedPractice) return null;

  const providers = practice?.providers || [];
  const configuredPayers = practice?.payers || [];
  const filteredProviders = providers.filter(p => {
    if (!providerSearch) return true;
    const term = providerSearch.toLowerCase();
    return (p.name || '').toLowerCase().includes(term) ||
           (p.npi || '').toLowerCase().includes(term) ||
           (p.taxonomy_code || '').toLowerCase().includes(term);
  });

  const payers = practice?.payers || [];
  const filteredPayers = payers.filter(p => {
    if (!payerSearchFilter) return true;
    const term = payerSearchFilter.toLowerCase();
    return (p.payer_name || '').toLowerCase().includes(term) ||
           (p.payer_id || '').toLowerCase().includes(term);
  });

  const filteredPatients = patients.filter(p => {
    if (!patientSearch) return true;
    const term = patientSearch.toLowerCase();
    return (p.display_name || '').toLowerCase().includes(term) ||
           (p.subscriber_id || '').toLowerCase().includes(term);
  });

  const fmtCount = (n) => Number(n || 0).toLocaleString('en-US');
  const tabs = [
    { id: 'info', label: 'Practice Info' },
    // How data reaches Atlas: the systems, the Data table, API keys (2026-10-25).
    ...(readOnly ? [] : [{ id: 'integrations', label: 'Integrations' }]),
    { id: 'providers', label: `Providers (${fmtCount(providers.length)})` },
    { id: 'payers', label: `Payers (${fmtCount(payers.length)})` },
    // Tabs regrouped by topic (David, 2026-10-25). The Patients tab is gone (the Patients
    // page is the one place for patients; its code below is unreachable until deleted)
    // and so is the Outreach templates tab (no patient outreach). "Practice Rules" split
    // into Billing Settings and Notifications. Each card keeps its own role gate inside
    // its tab; the endpoints carry their own role checks, so tabs are presentation.
    // Same gate the old Setup > Fee Schedule nav entry had: admin, and the feature on.
    ...(isAdmin && capabilityGate.features.fee_schedule ? [{ id: 'feeSchedule', label: 'Fee Schedule' }] : []),
    // The clinic gets Billing Settings too, for its coding standards only (2026-10-28).
    { id: 'billingSettings', label: 'Billing Settings' },
    ...(readOnly ? [] : [{ id: 'notifications', label: 'Notifications' }]),
    ...(isAdmin ? [{ id: 'features', label: 'Features' }] : []),
  ];

  return (
    <div className="practice-details-page">
      {/* readOnly is the client view, where the nav calls this page Clinic Details. */}
      <h1 className="page-title">{readOnly ? 'Clinic Details' : 'Practice Details'}</h1>

      {setupError && (
        <div className="form-message error dismissable">
          <span className="message-content">{setupError}</span>
          <button type="button" className="message-close" onClick={() => setSetupError(null)}>&times;</button>
        </div>
      )}
      {setupItems.length > 0 && (
        <div className="form-message warning setup-banner">
          <strong>Setup: {setupItems.length} {setupItems.length === 1 ? 'Item' : 'Items'} Open</strong>
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setShowSetup(true)}>Continue setup</button>
        </div>
      )}
      {showSetup && (
        <PracticeSetupWizard
          practiceId={selectedPractice.id}
          initialStep={setupItems[0] ? setupItems[0].step : 'summary'}
          onClose={() => { setShowSetup(false); loadSetupItems(); if (onPracticeUpdated) onPracticeUpdated(); }}
        />
      )}

      <div className="detail-tabs">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="detail-content">
        {/* Practice Info Tab */}
        {activeTab === 'info' && (
          <div className="info-tab">
            <form onSubmit={handleSaveInfo}>
              {infoMessage && (
                <div className={`form-message ${infoMessage.type}`}>{infoMessage.text}</div>
              )}
              <div className="form-row">
                <div className="form-field full">
                  <label>Practice Name *</label>
                  <input value={infoForm.name || ''} onChange={(e) => setInfoForm({ ...infoForm, name: e.target.value })} required />
                </div>
              </div>
              <div className="form-row">
                <div className="form-field">
                  <label>Organization NPI</label>
                  <input
                    value={infoForm.npi || ''}
                    onChange={(e) => setInfoForm({ ...infoForm, npi: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                    placeholder="10 digits"
                    maxLength={10}
                  />
                </div>
                <div className="form-field">
                  <label>Tax ID</label>
                  <input
                    value={infoForm.tax_id || ''}
                    onChange={(e) => setInfoForm({ ...infoForm, tax_id: formatTaxId(e.target.value) })}
                    placeholder="XX-XXXXXXX"
                    maxLength={10}
                  />
                </div>
                <div className="form-field">
                  <label>Phone</label>
                  <input value={infoForm.phone || ''} onChange={(e) => setInfoForm({ ...infoForm, phone: formatPhone(e.target.value) })} placeholder="XXX-XXX-XXXX" maxLength={12} />
                </div>
                <div className="form-field">
                  <label>PTAN</label>
                  <input
                    value={infoForm.ptan || ''}
                    onChange={(e) => setInfoForm({ ...infoForm, ptan: e.target.value.toUpperCase().slice(0, 20) })}
                    placeholder="Medicare PTAN"
                    maxLength={20}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-field full">
                  <label>Address</label>
                  <input value={infoForm.address1 || ''} onChange={(e) => setInfoForm({ ...infoForm, address1: e.target.value })} placeholder="Street address" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-field">
                  <label>Suite / Unit</label>
                  <input value={infoForm.address2 || ''} onChange={(e) => setInfoForm({ ...infoForm, address2: e.target.value })} />
                </div>
                <div className="form-field">
                  <label>City</label>
                  <input value={infoForm.city || ''} onChange={(e) => setInfoForm({ ...infoForm, city: e.target.value })} />
                </div>
                <div className="form-field small">
                  <label>State</label>
                  <select value={infoForm.state || ''} onChange={(e) => setInfoForm({ ...infoForm, state: e.target.value })}>
                    <option value="">--</option>
                    {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="form-field small">
                  <label>ZIP</label>
                  <input
                    value={infoForm.zip || ''}
                    onChange={(e) => setInfoForm({ ...infoForm, zip: formatZip(e.target.value) })}
                    placeholder="12345"
                    maxLength={10}
                  />
                </div>
              </div>
              {!readOnly && (
                <div className="form-actions">
                  <button type="submit" className="btn btn-primary" disabled={savingInfo}>
                    {savingInfo ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              )}
            </form>

            {/* Clients upload their own logo here; the logo route allows it for their practices. */}
            <PracticeLogoCard practiceId={selectedPractice.id} />


            {/* Contract Details */}
            {SHOW_CONTRACT_DETAILS && !readOnly && (
            <div className="integrations-section">
              <h3>Contract Details</h3>
              {contractMessage && (
                <div className={`field-message ${contractMessage.type}`} style={{ marginBottom: 12 }}>
                  {contractMessage.text}
                  <button className="message-close-sm" onClick={() => setContractMessage(null)}>&times;</button>
                </div>
              )}
              <table className="detail-table">
                <tbody>
                  <tr>
                    <td className="detail-label">Atlas Rate</td>
                    <td>
                      <div className="contract-rate-input">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.01"
                          placeholder="e.g. 8"
                          value={contractRate}
                          disabled={savingContract}
                          onChange={(e) => setContractRate(e.target.value)}
                          onBlur={async () => {
                            const orig = practice?.collections_fee_pct;
                            const origStr = orig !== null && orig !== undefined ? String(orig) : '';
                            if (contractRate === origStr) return;
                            setSavingContract(true);
                            setContractMessage(null);
                            try {
                              const body = { collections_fee_pct: contractRate === '' ? null : contractRate };
                              const response = await fetch(`${API_URL}/api/practices/${selectedPractice.id}`, {
                                method: 'PUT',
                                headers: { 'Content-Type': 'application/json' },
                                credentials: 'include',
                                body: JSON.stringify(body),
                              });
                              if (response.ok) {
                                const data = await response.json();
                                setPractice(data);
                                setContractMessage({ type: 'success', text: 'Saved.' });
                              } else {
                                const err = await response.json().catch(() => ({}));
                                setContractMessage({ type: 'error', text: err.error || 'Failed to save' });
                              }
                            } catch {
                              setContractMessage({ type: 'error', text: 'Failed to save' });
                            } finally {
                              setSavingContract(false);
                            }
                          }}
                        />
                        <span className="contract-rate-suffix">%</span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            )}

          </div>
        )}

        {/* Integrations Tab */}
        {!readOnly && activeTab === 'integrations' && (
          <div className="info-tab">
            <div className="integrations-section">
              <h3>Integrations</h3>
              {integrationMessage && (
                <div className={`field-message ${integrationMessage.type}`} style={{ marginBottom: 12 }}>
                  {integrationMessage.text}
                  <button className="message-close-sm" onClick={() => setIntegrationMessage(null)}>&times;</button>
                </div>
              )}
              <table className="detail-table integrations-table">
                <thead>
                  <tr>
                    <th className="int-col-type">Type</th>
                    <th className="int-col-system">System</th>
                    <th className="int-col-creds">Credentials</th>
                    <th className="int-col-sync">Sync</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    {
                      type: 'clearinghouse', label: 'Clearinghouse',
                      system: practice?.clearinghouse || infoForm.clearinghouse || '',
                      hasCreds: practice?.has_clearinghouse_credentials,
                      apiSystems: ['stedi', 'claimmd'],
                      options: [
                        { value: '', label: 'None' },
                        { value: 'waystar', label: 'Waystar' },
                        { value: 'change', label: 'Change' },
                        { value: 'trizetto', label: 'TriZetto' },
                        { value: 'officeally', label: 'Office Ally' },
                        { value: 'claimmd', label: 'Claim.MD' },
                        { value: 'stedi', label: 'Stedi' },
                        { value: 'other', label: 'Other' },
                      ],
                      displayName: {
                        waystar: 'Waystar', change: 'Change', trizetto: 'TriZetto',
                        officeally: 'Office Ally', stedi: 'Stedi', claimmd: 'Claim.MD', other: 'Other',
                      },
                      features: [{ key: 'api', label: 'API' }],
                      onChangeSystem: async (val) => {
                        setInfoForm(f => ({ ...f, clearinghouse: val }));
                        setSavingIntegration('clearinghouse');
                        try {
                          const payload = { clearinghouse: val };
                          if (!val) payload.clearinghouse_api_key = '';
                          const response = await fetch(`${API_URL}/api/practices/${selectedPractice.id}`, {
                            method: 'PUT', headers: { 'Content-Type': 'application/json' },
                            credentials: 'include', body: JSON.stringify(payload),
                          });
                          if (response.ok) { const data = await response.json(); setPractice(data); }
                          else { setIntegrationMessage({ type: 'error', text: 'Failed to save' }); }
                        } catch { setIntegrationMessage({ type: 'error', text: 'Failed to save' }); }
                        finally { setSavingIntegration(null); }
                      },
                    },
                    {
                      type: 'emr', label: 'EMR',
                      system: practice?.emr_system || '',
                      hasCreds: practice?.has_emr_credentials,
                      // Practice Fusion routes through Cobalt exactly as eCW does (the
                      // Configure dialog takes Cobalt's three credentials): Soldano, 2026-10-26.
                      apiSystems: ['tebra', 'ecw', 'practicebetter', 'practicefusion'],
                      options: [
                        { value: '', label: 'None' },
                        { value: 'athena', label: 'Athena' },
                        { value: 'ecw', label: 'eClinicalWorks' },
                        { value: 'practicefusion', label: 'Practice Fusion' },
                        { value: 'practicebetter', label: 'PracticeBetter' },
                        { value: 'tebra', label: 'Tebra' },
                        { value: 'modmed', label: 'ModMed' },
                        { value: 'other', label: 'Other' },
                      ],
                      displayName: {
                        athena: 'Athena', ecw: 'eClinicalWorks', practicefusion: 'Practice Fusion',
                        practicebetter: 'PracticeBetter', tebra: 'Tebra',
                        modmed: 'ModMed', other: 'Other',
                      },
                      features: [{ key: 'api', label: 'API' }, { key: 'appointments', label: 'Appointments' }],
                      onChangeSystem: async (val) => {
                        const prev = practice?.emr_system || '';
                        setPractice(p => ({ ...p, emr_system: val || null, has_emr_credentials: val ? p.has_emr_credentials : false }));
                        setSavingIntegration('emr');
                        try {
                          const response = await fetch(`${API_URL}/api/practices/${selectedPractice.id}`, {
                            method: 'PUT', headers: { 'Content-Type': 'application/json' },
                            credentials: 'include', body: JSON.stringify({ emr_system: val }),
                          });
                          if (response.ok) { const data = await response.json(); setPractice(data); }
                          else { setPractice(p => ({ ...p, emr_system: prev || null })); setIntegrationMessage({ type: 'error', text: 'Failed to save EMR' }); }
                        } catch { setPractice(p => ({ ...p, emr_system: prev || null })); setIntegrationMessage({ type: 'error', text: 'Failed to save' }); }
                        finally { setSavingIntegration(null); }
                      },
                    },
                    {
                      type: 'pm', label: 'Practice Management',
                      system: practice?.pm_system || '',
                      hasCreds: practice?.has_pm_credentials,
                      apiSystems: ['practicesuite', 'tebra'],
                      options: [
                        { value: '', label: 'None' },
                        { value: 'practicesuite', label: 'PracticeSuite' },
                        { value: 'openpm', label: 'OpenPM' },
                        { value: 'advancedmd', label: 'AdvancedMD' },
                        { value: 'ecw', label: 'eClinicalWorks' },
                        { value: 'tebra', label: 'Tebra' },
                        { value: 'other', label: 'Other' },
                      ],
                      displayName: {
                        practicesuite: 'PracticeSuite', openpm: 'OpenPM', advancedmd: 'AdvancedMD',
                        ecw: 'eClinicalWorks', tebra: 'Tebra', other: 'Other',
                      },
                      features: [{ key: 'api', label: 'API' }],
                      onChangeSystem: async (val) => {
                        const prev = practice?.pm_system || '';
                        setPractice(p => ({ ...p, pm_system: val || null, has_pm_credentials: val ? p.has_pm_credentials : false }));
                        setSavingIntegration('pm');
                        try {
                          const response = await fetch(`${API_URL}/api/practices/${selectedPractice.id}`, {
                            method: 'PUT', headers: { 'Content-Type': 'application/json' },
                            credentials: 'include', body: JSON.stringify({ pm_system: val }),
                          });
                          if (response.ok) { const data = await response.json(); setPractice(data); }
                          else { setPractice(p => ({ ...p, pm_system: prev || null })); setIntegrationMessage({ type: 'error', text: 'Failed to save PM' }); }
                        } catch { setPractice(p => ({ ...p, pm_system: prev || null })); setIntegrationMessage({ type: 'error', text: 'Failed to save' }); }
                        finally { setSavingIntegration(null); }
                      },
                    },
                  ].map((row) => {
                    const hasApi = row.apiSystems.includes(row.system);
                    return (
                      <tr key={row.type}>
                        <td className="int-type-label">{row.label}</td>
                        <td>
                          {readOnly ? (
                            <span>{row.displayName[row.system] || <span className="text-muted">None</span>}</span>
                          ) : (
                            <select
                              className="int-select"
                              value={row.system}
                              disabled={savingIntegration === row.type}
                              onChange={(e) => row.onChangeSystem(e.target.value)}
                            >
                              {row.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                          )}
                        </td>
                        <td>
                          {!row.system || readOnly ? (
                            <span className="int-cred-btn disabled">
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                              {readOnly && row.hasCreds ? 'Configured' : '—'}
                            </span>
                          ) : (
                            <button
                              className={`int-cred-btn${row.hasCreds ? ' configured' : ''}`}
                              onClick={() => openCredentialsModal(row.type, row.system)}
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                              {row.hasCreds ? 'Update' : 'Configure'}
                            </button>
                          )}
                        </td>
                        <td className="int-col-sync">
                          {row.type === 'clearinghouse' && hasApi && row.hasCreds && !readOnly ? (
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={runClearinghouseSync}
                              disabled={syncingClearinghouse}
                            >
                              {syncingClearinghouse ? 'Syncing…' : 'Sync now'}
                            </button>
                          ) : row.type === 'emr' && row.system === 'practicebetter' && hasApi && row.hasCreds && !readOnly ? (
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={runPracticebetterSync}
                              disabled={syncingPracticebetter}
                            >
                              {syncingPracticebetter ? 'Syncing…' : 'Sync now'}
                            </button>
                          ) : (
                            <span className="int-sync-disabled">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Data: what arrives for this practice, from where, how fresh — right under
                Integrations, with the uploads and feeds one button away (David,
                2026-10-25; replaces the Setup > Data page in the nav and the coverage
                table that sat further down this page). Not in the client view. */}
            <DataFeedsTable practiceId={selectedPractice.id} selectedPractice={selectedPractice} />

            {/* A Google Sheet as the practice's feed (Olympus-style); shows itself only
                for a sheet practice or one with no feed yet. */}
            {!capabilityGate.loading && (
              <SheetConnectorCard practice={practice} source={capabilityGate.source} onChanged={capabilityGate.refresh} />
            )}
            {/* Machine API keys: outside systems that connect to Atlas. Admin only. */}
            {/* How claims leave Atlas for a portal practice (the submit queue): read-only. */}
            <SubmissionCard practiceId={selectedPractice.id} />
            {/* The number this practice's patients are texted from: the shared Atlas
                number unless an override is saved here (Jordan, 2026-10-26). */}
            <SmsNumberCard practiceId={selectedPractice.id} />
            {/* The practice's staff contacts and the text thread with each (client
                texts, docs/client-texts.md). Any staff member chats; admins edit. */}
            <div id="client-texts">
              <ContactsCard practiceId={selectedPractice.id} isAdmin={isAdmin} practiceName={selectedPractice.name} />
            </div>
            {isAdmin && <ApiKeysPanel practiceId={selectedPractice.id} isAdmin={isAdmin} />}

            {/* Credentials Modal */}
            {credentialsModal && (
              <div className="modal-overlay" onClick={() => setCredentialsModal(null)}>
                <div className="create-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 460 }}>
                  <h2>
                    {credentialsModal.system === 'tebra' ? 'Tebra' :
                     credentialsModal.system === 'stedi' ? 'Stedi' :
                     credentialsModal.system === 'claimmd' ? 'Claim.MD' :
                     credentialsModal.system === 'practicesuite' ? 'PracticeSuite' :
                     credentialsModal.system === 'practicebetter' ? 'PracticeBetter' :
                     credentialsModal.system === 'ecw' ? 'eClinicalWorks' :
                     credentialsModal.system === 'practicefusion' ? 'Practice Fusion (Cobalt)' :
                     credentialsModal.system} Credentials
                  </h2>
                  {!credentialSchema ? (
                    <div className="loading-state" style={{ padding: '24px' }}>
                      <div className="spinner" />
                      <span>Loading...</span>
                    </div>
                  ) : (
                    <>
                      <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
                        Enter credentials for this integration. Values are encrypted at rest.
                      </p>
                      {credentialSchema.map(field => (
                        <div className="form-field" key={field.key} style={{ marginBottom: 12 }}>
                          <label>{field.label}{field.required ? ' *' : ''}</label>
                          <input
                            type={field.type === 'password' ? 'password' : 'text'}
                            value={credentialForm[field.key] || ''}
                            onChange={(e) => setCredentialForm(f => ({ ...f, [field.key]: e.target.value }))}
                            placeholder={field.placeholder || field.label}
                            autoComplete="off"
                          />
                        </div>
                      ))}
                      <div className="modal-actions">
                        <button className="btn btn-secondary" onClick={() => setCredentialsModal(null)}>Cancel</button>
                        <button
                          className="btn btn-primary"
                          onClick={handleSaveCredentials}
                          disabled={savingCredentials || !credentialSchema.filter(f => f.required).every(f => (credentialForm[f.key] || '').trim())}
                        >
                          {savingCredentials ? 'Saving...' : 'Save Credentials'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Providers Tab */}
        {activeTab === 'providers' && (
          <div className="providers-tab">
            <div className="tab-header">
              <input
                type="text"
                className="tab-search-input"
                placeholder="Search providers..."
                value={providerSearch}
                onChange={(e) => setProviderSearch(e.target.value)}
              />
              {/* Clients add and edit their own providers (David, 2026-10-21); removing
                  one stays with staff, and the backend enforces both. */}
              <button className="btn btn-primary" onClick={() => {
                setEditingProvider(null);
                setProviderError(null);
                setNpiLooked(false);
                setProviderForm({ first_name: '', last_name: '', npi: '', taxonomy_code: '', provider_type: 'PT', gender: '' });
                setShowProviderForm(true);
              }}>
                Add Provider
              </button>
            </div>

            {providers.length === 0 ? (
              <div className="empty-message">No providers configured. Add a provider to get started.</div>
            ) : (
              <table className="detail-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>NPI</th>
                    <th>Type</th>
                    <th>Taxonomy</th>
                    <th>Gender</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProviders.map((p) => (
                    <tr key={p.id}>
                      <td className="col-name">{p.name}</td>
                      <td>{p.npi}</td>
                      <td>{p.provider_type}</td>
                      <td>
                        {p.taxonomy_code}
                        {taxonomyNames[p.npi]?.[p.taxonomy_code] && (
                          <div className="col-subtext">{taxonomyNames[p.npi][p.taxonomy_code]}</div>
                        )}
                      </td>
                      <td>{p.gender || '-'}</td>
                      <td className="col-actions">
                        <button className="btn-link" onClick={() => handleEditProvider(p)}>Edit</button>
                        {!readOnly && (
                          <button className="btn-link danger" onClick={() => handleDeleteProvider(p.id)}>Remove</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* Provider Form Modal */}
            {showProviderForm && (
              <div className="modal-overlay" onClick={() => setShowProviderForm(false)}>
                <div className="create-modal" onClick={(e) => e.stopPropagation()}>
                  <h2>{editingProvider ? 'Edit Provider' : 'Add Provider'}</h2>
                  {providerError && <div className="form-message error">{providerError}</div>}
                  <form onSubmit={handleSaveProvider}>
                    {/* NPI lookup - primary entry method */}
                    {!editingProvider && (
                      <div className="npi-lookup-row">
                        <div className="form-field">
                          <label>NPI *</label>
                          <div className="npi-input-group">
                            <input
                              value={providerForm.npi}
                              onChange={(e) => { setProviderForm({ ...providerForm, npi: e.target.value.replace(/\D/g, '').slice(0, 10) }); setNpiLooked(false); }}
                              placeholder="Enter 10-digit NPI"
                              maxLength={10}
                              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleNpiLookup(); } }}
                            />
                            <button type="button" className="btn btn-primary" onClick={handleNpiLookup} disabled={lookingUpNpi || providerForm.npi.length !== 10}>
                              {lookingUpNpi ? 'Looking up...' : 'Lookup'}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    {/* Show remaining fields after lookup or when editing */}
                    {(npiLooked || editingProvider) && (<>
                    <div className="form-row">
                      {providerForm.provider_type === ORGANIZATION_PROVIDER_TYPE ? (
                        <div className="form-field">
                          <label>Organization Name *</label>
                          <input value={providerForm.last_name} onChange={(e) => setProviderForm({ ...providerForm, last_name: e.target.value })} required />
                        </div>
                      ) : (<>
                        <div className="form-field">
                          <label>First Name *</label>
                          <input value={providerForm.first_name} onChange={(e) => setProviderForm({ ...providerForm, first_name: e.target.value })} required />
                        </div>
                        <div className="form-field">
                          <label>Last Name *</label>
                          <input value={providerForm.last_name} onChange={(e) => setProviderForm({ ...providerForm, last_name: e.target.value })} required />
                        </div>
                      </>)}
                    </div>
                    {editingProvider && (
                      <div className="form-row">
                        <div className="form-field">
                          <label>NPI</label>
                          <input value={providerForm.npi} onChange={(e) => setProviderForm({ ...providerForm, npi: e.target.value.replace(/\D/g, '').slice(0, 10) })} placeholder="10 digits" maxLength={10} />
                        </div>
                      </div>
                    )}
                    <div className="form-row">
                      <div className="form-field">
                        <label>Taxonomy Code</label>
                        <input value={providerForm.taxonomy_code} onChange={(e) => setProviderForm({ ...providerForm, taxonomy_code: e.target.value.toUpperCase() })} placeholder="e.g., 225100000X" maxLength={11} />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-field">
                        <label>Provider Type</label>
                        <select value={providerForm.provider_type} onChange={(e) => setProviderForm({ ...providerForm, provider_type: e.target.value })}>
                          {PROVIDER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                          {(practice?.service_model === 'home_health' || providerForm.provider_type === ORGANIZATION_PROVIDER_TYPE) && (
                            <option value={ORGANIZATION_PROVIDER_TYPE}>{ORGANIZATION_PROVIDER_TYPE}</option>
                          )}
                        </select>
                      </div>
                      <div className="form-field small">
                        <label>Gender</label>
                        <select value={providerForm.gender} onChange={(e) => setProviderForm({ ...providerForm, gender: e.target.value })}>
                          <option value="">-</option>
                          <option value="M">M</option>
                          <option value="F">F</option>
                        </select>
                      </div>
                    </div>
                    <div className="modal-actions">
                      <button type="button" className="btn btn-secondary" onClick={() => setShowProviderForm(false)}>Cancel</button>
                      <button type="submit" className="btn btn-primary" disabled={savingProvider}>
                        {savingProvider ? 'Saving...' : (editingProvider ? 'Save Changes' : 'Add Provider')}
                      </button>
                    </div>
                    </>)}
                  </form>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Payers Tab */}
        {activeTab === 'payers' && (
          <div className="payers-tab">
            <div className="tab-header">
              <input
                type="text"
                className="tab-search-input"
                placeholder="Search payers..."
                value={payerSearchFilter}
                onChange={(e) => setPayerSearchFilter(e.target.value)}
              />
              {!readOnly && (
                <button className="btn btn-primary" onClick={() => {
                  setEditingPayerId(null);
                  setShowPayerForm(true);
                  setPayerError(null);
                  setPayerSearch('');
                  setPayerResults([]);
                  setSelectedStediPayer(null);
                  setIsCustomPayer(false);
                  setCustomPayerName('');
                  setCustomPayerId('');
                  setPayerDetails(emptyPayerDetails);
                }}>
                  Add Payer
                </button>
              )}
            </div>

            {payerIdError && (
              <div className="payer-id-error" role="alert">
                {payerIdError}
                <button type="button" onClick={() => setPayerIdError(null)} aria-label="Dismiss">&times;</button>
              </div>
            )}
            {payers.length === 0 ? (
              <div className="empty-message">No payers configured. Add the insurances this practice works with.</div>
            ) : (
              <table className="detail-table">
                <thead>
                  <tr>
                    <th></th>
                    <th>Payer Name</th>
                    <th>Payer ID</th>
                    <th>Portal</th>
                    <th>Network</th>
                    {/* The client view leaves authorization out for now (David, 2026-10-21). */}
                    {!readOnly && <th>Auth</th>}
                    <th>EDI</th>
                    {!readOnly && <th></th>}
                  </tr>
                </thead>
                <tbody>
                  {filteredPayers.map((p) => {
                    const needsId = !(p.payer_id || '').trim();
                    const isExpanded = expandedPayerId === p.id;
                    const colSpan = readOnly ? 6 : 8;
                    return (
                      <React.Fragment key={p.id}>
                      <tr className={needsId ? 'payer-row-needs-id' : ''}>
                        <td className="col-expand">
                          <button
                            className="payer-expand-btn"
                            onClick={() => setExpandedPayerId(isExpanded ? null : p.id)}
                            aria-label={isExpanded ? 'Collapse' : 'Expand'}
                          >
                            {isExpanded ? '▾' : '▸'}
                          </button>
                        </td>
                        <td className="col-name">
                          {p.payer_name}
                          {p.is_active === false && <span className="payer-inactive-badge">● Inactive</span>}
                          {needsId && <span className="payer-needs-id-label">Needs ID</span>}
                          {p.payer_node && <span className="payer-node-badge" title="Rules engine payer family">{p.payer_node.label}</span>}
                        </td>
                        <td className="col-mono col-payer-id">
                          <PayerIdInput
                            practiceId={selectedPractice.id}
                            payer={p}
                            onSaved={() => { setPayerIdError(null); loadPractice(); if (onPracticeUpdated) onPracticeUpdated(); }}
                            onError={setPayerIdError}
                          />
                        </td>
                        <td>
                          {p.portal_url
                            ? <a href={p.portal_url} target="_blank" rel="noreferrer">Link</a>
                            : <span className="text-muted">–</span>}
                        </td>
                        <td>
                          {p.in_network !== false
                            ? <span className="status-badge active">In Network</span>
                            : <span className="status-badge inactive">Out of Network</span>}
                        </td>
                        {!readOnly && (
                        <td>
                          {p.requires_auth === true ? <span className="status-badge inactive">Auth</span>
                            : p.requires_auth === false ? <span className="text-muted">No</span>
                            : <span className="text-muted">—</span>}
                        </td>
                        )}
                        <td className="payer-enroll-cell">
                          <span className={p.era_setup ? 'status-badge active' : 'status-badge inactive'}>ERA {p.era_setup ? '✓' : '✗'}</span>
                          <span className={p.eft_setup ? 'status-badge active' : 'status-badge inactive'}>EFT {p.eft_setup ? '✓' : '✗'}</span>
                        </td>
                        {!readOnly && (
                          <td className="col-actions">
                            <button className="btn-link" onClick={() => openEditPayer(p)}>Edit</button>
                          </td>
                        )}
                      </tr>
                      {isExpanded && (
                        <tr className="payer-detail-row">
                          <td colSpan={colSpan}>
                            <div className="payer-detail-grid">
                              {!readOnly && (
                              <div className="payer-detail-section">
                                <h4>Authorization</h4>
                                <div><span className="pd-label">Requires auth:</span> {p.requires_auth === true ? 'Yes' : p.requires_auth === false ? 'No' : 'Unknown'}</div>
                                <div><span className="pd-label">Submission:</span> {p.auth_submission_method || '—'}</div>
                                <div><span className="pd-label">Notes:</span> {p.auth_detail || '—'}</div>
                              </div>
                              )}
                              <div className="payer-detail-section">
                                <h4>Portal</h4>
                                <div><span className="pd-label">Username:</span> {p.portal_username || '—'}</div>
                                <div><span className="pd-label">Password:</span>{' '}
                                  {!p.portal_password_set ? <span className="text-muted">not set</span>
                                    : revealedPw[p.id] != null ? (
                                      <>
                                        <span className="col-mono">{revealedPw[p.id]}</span>{' '}
                                        <button className="btn-link" onClick={() => hidePortalPassword(p.id)}>Hide</button>
                                      </>
                                    ) : (
                                      <>
                                        <span className="col-mono">••••••••</span>{' '}
                                        <button className="btn-link" onClick={() => revealPortalPassword(p.id)}>Reveal</button>
                                      </>
                                    )}
                                </div>
                              </div>
                              <div className="payer-detail-section">
                                <h4>Contacts</h4>
                                <div><span className="pd-label">Phone:</span> {p.phone || '—'}</div>
                                <div><span className="pd-label">Fax:</span> {p.fax || '—'}</div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}

            {/* Add / Edit Payer Modal */}
            {showPayerForm && (
              <div className="modal-overlay" onClick={closePayerForm}>
                <div className="create-modal payer-edit-modal" onClick={(e) => e.stopPropagation()}>
                  <h2>{editingPayerId != null
                    ? (payers.find((pp) => pp.id === editingPayerId)?.payer_name || 'Edit Payer')
                    : 'Add Payer'}</h2>
                  {editingPayerId != null && (
                    <div className="payer-modal-subtitle">Payer ID: {payers.find((pp) => pp.id === editingPayerId)?.payer_id || '—'}</div>
                  )}
                  {payerError && <div className="form-message error">{payerError}</div>}

                  {editingPayerId == null && (
                    <>
                      <div className="payer-mode-toggle">
                        <label className="toggle-label">
                          <input
                            type="checkbox"
                            checked={isCustomPayer}
                            onChange={(e) => {
                              setIsCustomPayer(e.target.checked);
                              setPayerSearch('');
                              setPayerResults([]);
                              setSelectedStediPayer(null);
                              setCustomPayerName('');
                              setCustomPayerId('');
                            }}
                          />
                          <span>Not Listed (custom entry)</span>
                        </label>
                      </div>

                      {!isCustomPayer ? (
                        <div className="payer-search-section">
                          <div className="form-field">
                            <label>Search Stedi Payer Directory</label>
                            <input
                              value={payerSearch}
                              onChange={(e) => handlePayerSearch(e.target.value)}
                              placeholder="Type payer name (e.g., Blue Cross, Aetna)..."
                              autoFocus
                            />
                          </div>
                          {searchingPayers && (
                            <div className="payer-search-status">Searching...</div>
                          )}
                          {payerResults.length > 0 && !selectedStediPayer && (
                            <div className="payer-search-results">
                              {payerResults.map((p, i) => (
                                <div
                                  key={i}
                                  className="payer-result-item"
                                  onClick={() => handleSelectStediPayer(p)}
                                >
                                  <div className="payer-result-name">{p.name}</div>
                                  <div className="payer-result-id">ID: {p.payer_id}</div>
                                </div>
                              ))}
                            </div>
                          )}
                          {selectedStediPayer && (
                            <div className="payer-selected">
                              <div className="payer-selected-name">{selectedStediPayer.name}</div>
                              <div className="payer-selected-id">Payer ID: {selectedStediPayer.payer_id}</div>
                              <button className="btn-link" onClick={() => { setSelectedStediPayer(null); setPayerSearch(''); }}>Change</button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="payer-custom-section">
                          <div className="form-field">
                            <label>Payer Name *</label>
                            <input
                              value={customPayerName}
                              onChange={(e) => setCustomPayerName(e.target.value)}
                              placeholder="Enter payer name"
                              autoFocus
                            />
                          </div>
                          <div className="form-field" style={{ marginTop: 12 }}>
                            <label>Payer ID *</label>
                            <input
                              value={customPayerId}
                              onChange={(e) => setCustomPayerId(e.target.value)}
                              placeholder="Trading partner service ID"
                            />
                          </div>
                        </div>
                      )}
                    </>
                  )}

                  <div className="payer-detail-fields">
                    <div className="payer-form-row">
                      <div className="form-field">
                        <label>Network status</label>
                        <select value={payerDetails.in_network ? 'in' : 'out'} onChange={(e) => setPD('in_network', e.target.value === 'in')}>
                          <option value="in">In network</option>
                          <option value="out">Out of network</option>
                        </select>
                      </div>
                      <div className="form-field">
                        <label>Payer status</label>
                        <select value={payerDetails.is_active ? 'active' : 'inactive'} onChange={(e) => setPD('is_active', e.target.value === 'active')}>
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </div>
                    </div>
                    <div className="payer-form-row">
                      <PayerNodePicker
                        value={payerDetails.payer_node_id}
                        onChange={(id) => setPD('payer_node_id', id)}
                        current={payerDetails.payer_node}
                      />
                    </div>

                    <h3 className="payer-form-group-title">Authorization</h3>
                    <div className="payer-auth-grid">
                      <div className="payer-auth-left">
                        <div className="form-field">
                          <label>Requires auth?</label>
                          <select value={payerDetails.requires_auth} onChange={(e) => setPD('requires_auth', e.target.value)}>
                            <option value="">Unknown</option>
                            <option value="yes">Yes</option>
                            <option value="no">No</option>
                          </select>
                        </div>
                        <div className="form-field">
                          <label>Submission method</label>
                          <select value={payerDetails.auth_submission_method} onChange={(e) => setPD('auth_submission_method', e.target.value)}>
                            <option value="">—</option>
                            <option value="phone">Phone</option>
                            <option value="fax">Fax</option>
                            <option value="portal">Portal</option>
                          </select>
                        </div>
                      </div>
                      <div className="form-field payer-auth-notes">
                        <label>Notes</label>
                        <textarea value={payerDetails.auth_detail} onChange={(e) => setPD('auth_detail', e.target.value)} placeholder="Which services need auth, how, turnaround..." />
                      </div>
                    </div>

                    <h3 className="payer-form-group-title">Portal</h3>
                    <div className="payer-form-row">
                      <div className="form-field">
                        <label>Username</label>
                        <input value={payerDetails.portal_username} onChange={(e) => setPD('portal_username', e.target.value)} autoComplete="off" />
                      </div>
                      <div className="form-field">
                        <label>Password</label>
                        <input
                          type="password"
                          value={payerDetails.portal_password}
                          onChange={(e) => setPD('portal_password', e.target.value)}
                          autoComplete="new-password"
                          placeholder={payerDetails.portal_password_set ? '••••••••' : ''}
                        />
                      </div>
                    </div>
                    <div className="form-field">
                      <label>URL</label>
                      <input value={payerDetails.portal_url} onChange={(e) => setPD('portal_url', e.target.value)} placeholder="https://..." />
                    </div>

                    <h3 className="payer-form-group-title">Contacts</h3>
                    <div className="payer-form-row">
                      <div className="form-field">
                        <label>Phone</label>
                        <input value={payerDetails.phone} onChange={(e) => setPD('phone', e.target.value)} />
                      </div>
                      <div className="form-field">
                        <label>Fax</label>
                        <input value={payerDetails.fax} onChange={(e) => setPD('fax', e.target.value)} />
                      </div>
                    </div>

                    <h3 className="payer-form-group-title">EDI</h3>
                    <div className="payer-form-row">
                      <div className="form-field">
                        <label>ERA set up</label>
                        <select value={payerDetails.era_setup ? 'yes' : 'no'} onChange={(e) => setPD('era_setup', e.target.value === 'yes')}>
                          <option value="no">No</option>
                          <option value="yes">Yes</option>
                        </select>
                      </div>
                      <div className="form-field">
                        <label>EFT set up</label>
                        <select value={payerDetails.eft_setup ? 'yes' : 'no'} onChange={(e) => setPD('eft_setup', e.target.value === 'yes')}>
                          <option value="no">No</option>
                          <option value="yes">Yes</option>
                        </select>
                      </div>
                      <div className="form-field">
                        <label>ERA Portal</label>
                        <select value={payerDetails.era_portal} onChange={(e) => setPD('era_portal', e.target.value)}>
                          <option value="">—</option>
                          <option value="Availity">Availity</option>
                          <option value="Zelis">Zelis</option>
                          <option value="Echo">Echo</option>
                          <option value="Payspan">Payspan</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="modal-actions payer-modal-actions">
                    {editingPayerId != null && (
                      <button type="button" className="btn-link danger payer-delete-btn" onClick={handleDeleteFromEdit}>Remove payer</button>
                    )}
                    <div className="payer-modal-actions-right">
                      <button type="button" className="btn btn-secondary" onClick={closePayerForm}>Cancel</button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleSavePayer}
                        disabled={savingPayer || (editingPayerId == null && ((!isCustomPayer && !selectedStediPayer) || (isCustomPayer && (!customPayerName.trim() || !customPayerId.trim()))))}
                      >
                        {savingPayer
                          ? (editingPayerId != null ? 'Saving...' : 'Adding...')
                          : (editingPayerId != null ? 'Save Changes' : 'Add Payer')}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {/* Payer enrollments are off this tab (David, 2026-10-25): the payer table's
                Network column already says it. The authorization routes card is hidden
                for now; PayerEnrollmentsPanel and AuthRoutesCard are unchanged. */}
          </div>
        )}

        {/* Patients Tab */}
        {activeTab === 'patients' && (
          <div className="patients-tab">
            <div className="tab-header">
              <input
                type="text"
                className="tab-search-input"
                placeholder="Search patients..."
                value={patientSearch}
                onChange={(e) => setPatientSearch(e.target.value)}
              />
              {!readOnly && (
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button className="btn btn-secondary" onClick={() => {
                    setEditingPatient({ _isNew: true });
                    setPatientForm({
                      subscriber_id: '', subscriber_first_name: '', subscriber_last_name: '',
                      subscriber_dob: '', subscriber_gender: '', payer_id: '', payer_name: '',
                      patient_is_subscriber: 'Y', patient_first_name: '', patient_last_name: '',
                      patient_dob: '', patient_gender: '', patient_relationship_code: '',
                      phone: '', email: '', address1: '', address2: '', city: '', state: '', zip: '',
                      payment_type: 'unknown',
                      secondary_payer_id: '', secondary_payer_name: '', secondary_subscriber_id: '',
                      tertiary_payer_id: '', tertiary_payer_name: '', tertiary_subscriber_id: '',
                    });
                    setPatientError(null);
                  }}>
                    Add Patient
                  </button>
                  <button className="btn btn-primary" onClick={() => {
                    setShowUploadModal(true);
                    setUploadFile(null);
                    setUploadFormat(practice?.csv_format || '');
                    setRememberFormat(!!practice?.csv_format);
                    setUploadMessage(null);
                  }}>
                    Upload CSV
                  </button>
                </div>
              )}
            </div>

            {uploadMessage && (
              <div className={`form-message ${uploadMessage.type} dismissable`}>
                <div className="message-content">{uploadMessage.text}</div>
                <button className="message-close" onClick={() => setUploadMessage(null)}>&times;</button>
              </div>
            )}

            {loadingPatients ? (
              <div className="loading-state"><div className="spinner"></div><span>Loading patients...</span></div>
            ) : patients.length === 0 ? (
              <div className="empty-message">No patients. Upload a CSV to add patients to this practice.</div>
            ) : (
              <div className="patient-table-wrapper">
                <table className="detail-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Primary Payer</th>
                      <th>Member ID</th>
                      <th>Payer ID</th>
                      <th>DOB</th>
                      <th>Phone</th>
                      <th>Email</th>
                      {!readOnly && <th style={{ width: '100px' }}>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPatients.map((p) => {
                      const isSelfPay = p.payment_type === 'self_pay';
                      const isAutoWC = p.payment_type === 'auto_wc';
                      const isOther = p.payment_type === 'other';
                      const isMissingInsurance = (p.payment_type === 'insurance' || p.payment_type === 'unknown' || !p.payment_type) && !p.payer_id;
                      const primaryPayerLabel = isSelfPay ? 'Self-Pay' : isAutoWC ? 'Auto/WC' : isOther ? 'Other' : (p.payer_name || '-');
                      return (
                      <tr
                        key={p.id}
                        className={isMissingInsurance ? 'row-warning' : ''}
                        onClick={readOnly ? () => handleEditPatient(p) : undefined}
                        style={readOnly ? { cursor: 'pointer' } : undefined}
                      >
                        <td className="col-name">{p.display_name}</td>
                        <td className="col-payer">
                          {primaryPayerLabel}
                          {!isSelfPay && !isAutoWC && !isOther && p.eligibility_status === 'Active' && (
                            <span className="elig-inline">
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                                <circle cx="12" cy="12" r="11" fill="#00a67d"/>
                                <path d="M7 12.5l3 3 7-7" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                              <span className="elig-date">{p.eligibility_checked_at ? (() => { const [y,m,d] = p.eligibility_checked_at.split('-'); const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']; return months[parseInt(m,10)-1] + '. ' + parseInt(d,10); })() : ''}</span>
                            </span>
                          )}
                          {!isSelfPay && !isAutoWC && !isOther && p.eligibility_status === 'Inactive' && (
                            <span className="elig-inline">
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                                <circle cx="12" cy="12" r="11" fill="#d63031"/>
                                <path d="M8 8l8 8M16 8l-8 8" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"/>
                              </svg>
                              <span className="elig-date">{p.eligibility_checked_at ? (() => { const [y,m,d] = p.eligibility_checked_at.split('-'); const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']; return months[parseInt(m,10)-1] + '. ' + parseInt(d,10); })() : ''}</span>
                            </span>
                          )}
                        </td>
                        <td>{isSelfPay || isAutoWC || isOther ? '-' : (p.subscriber_id || '-')}</td>
                        <td>{isSelfPay || isAutoWC || isOther ? '-' : (p.payer_id || <span className="text-warning">unmapped</span>)}</td>
                        <td>{p.subscriber_dob || '-'}</td>
                        <td>{p.phone || '-'}</td>
                        <td className="col-email">{p.email || '-'}</td>
                        {!readOnly && (
                          <td className="actions-cell">
                            <button className="btn-icon" title="Edit" onClick={() => handleEditPatient(p)}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                              </svg>
                            </button>
                            <button className="btn-icon btn-icon-danger" title="Delete" onClick={() => handleDeletePatient(p)}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                              </svg>
                            </button>
                          </td>
                        )}
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Upload Modal */}
            {showUploadModal && (
              <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
                <div className="create-modal upload-modal" onClick={(e) => e.stopPropagation()}>
                  <h2>Upload Patients</h2>

                  <div className="form-field">
                    <label>CSV Source</label>
                    <select
                      value={uploadFormat}
                      onChange={(e) => setUploadFormat(e.target.value)}
                    >
                      <option value="">Auto-Detect</option>
                      <option value="practicesuite">PracticeSuite</option>
                      <option value="native">Atlas Native</option>
                    </select>
                  </div>

                  <div className="remember-toggle">
                    <label className="toggle-label">
                      <input
                        type="checkbox"
                        checked={rememberFormat}
                        onChange={(e) => setRememberFormat(e.target.checked)}
                        disabled={!uploadFormat}
                      />
                      <span>Remember for this practice</span>
                    </label>
                  </div>

                  <div className="upload-drop-zone">
                    <input
                      type="file"
                      accept=".csv"
                      onChange={(e) => setUploadFile(e.target.files[0])}
                      id="patient-csv-upload"
                      style={{ display: 'none' }}
                    />
                    <label htmlFor="patient-csv-upload" className="drop-zone-label">
                      {uploadFile ? (
                        <div className="drop-zone-file">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" />
                          </svg>
                          <span>{uploadFile.name}</span>
                          <span className="drop-zone-change">Change</span>
                        </div>
                      ) : (
                        <div className="drop-zone-empty">
                          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="17 8 12 3 7 8" />
                            <line x1="12" y1="3" x2="12" y2="15" />
                          </svg>
                          <span>Click to select a .csv file</span>
                        </div>
                      )}
                    </label>
                  </div>

                  {patients.length > 0 && (
                    <div className="upload-replace-warning">
                      This will replace all {patients.length} existing patients for this practice.
                    </div>
                  )}

                  <div className="modal-actions">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowUploadModal(false)}>Cancel</button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleUploadPatients}
                      disabled={!uploadFile || uploading}
                    >
                      {uploading ? 'Uploading...' : 'Upload'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Resolve Payers Modal */}
            {showResolveModal && (
              <div className="modal-overlay" onClick={() => setShowResolveModal(false)}>
                <div className="create-modal resolve-modal" onClick={(e) => e.stopPropagation()}>
                  <h2>Resolve Payer Mappings</h2>
                  <p className="resolve-description">
                    The following payer names from the CSV don't match your configured payers.
                    Map each to an existing payer, add a new one, or skip.
                  </p>

                  <div className="resolve-list">
                    {resolveItems.map((item, idx) => (
                      <div key={idx} className="resolve-item">
                        <div className="resolve-csv-name">"{item.csv_name}"</div>
                        <div className="resolve-arrow">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M5 12h14" /><polyline points="12 5 19 12 12 19" />
                          </svg>
                        </div>
                        <div className="resolve-select">
                          <select
                            value={item.selected_payer_db_id === 'new' ? 'new' : (item.selected_payer_db_id || '')}
                            onChange={(e) => {
                              const val = e.target.value;
                              const updated = [...resolveItems];
                              updated[idx] = {
                                ...item,
                                selected_payer_db_id: val === 'new' ? 'new' : (val ? parseInt(val) : ''),
                                new_payer_name: val === 'new' ? item.csv_name : '',
                                new_payer_id: '',
                              };
                              setResolveItems(updated);
                            }}
                          >
                            <option value="">-- Skip --</option>
                            {payers.map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.payer_name} ({p.payer_id})
                              </option>
                            ))}
                            <option value="new">+ Add new payer</option>
                          </select>
                          {item.selected_payer_db_id === 'new' && (
                            <div className="resolve-new-payer">
                              <input
                                placeholder="Payer name"
                                value={item.new_payer_name || ''}
                                onChange={(e) => {
                                  const updated = [...resolveItems];
                                  updated[idx] = { ...item, new_payer_name: e.target.value };
                                  setResolveItems(updated);
                                }}
                              />
                              <input
                                placeholder="Payer ID"
                                value={item.new_payer_id || ''}
                                onChange={(e) => {
                                  const updated = [...resolveItems];
                                  updated[idx] = { ...item, new_payer_id: e.target.value };
                                  setResolveItems(updated);
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="modal-actions">
                    <button type="button" className="btn btn-secondary" onClick={() => setShowResolveModal(false)}>
                      Skip All
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleResolvePayers}
                      disabled={resolving}
                    >
                      {resolving ? 'Saving...' : `Apply Mappings (${resolveItems.filter(i => i.selected_payer_db_id).length})`}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Patient Edit Modal */}
            {editingPatient && (
              <div className="modal-overlay" onClick={() => setEditingPatient(null)}>
                <div className="create-modal patient-edit-modal" onClick={(e) => e.stopPropagation()}>
                  <h2>{editingPatient?._isNew ? 'Add Patient' : (readOnly ? 'Patient Details' : 'Edit Patient')}</h2>

                  {patientError && (
                    <div className="form-message error">
                      <div className="message-content">{patientError}</div>
                    </div>
                  )}

                  <form onSubmit={handleSavePatient}>
                    <fieldset disabled={readOnly} style={{ border: 'none', padding: 0, margin: 0 }}>
                    {/* === PATIENT INFORMATION === */}
                    <div className="patient-section">
                      <div className="patient-section-title">Patient Information</div>

                      <div className="form-row-inline">
                        <div className="form-field" style={{ flex: 2 }}>
                          <label>First Name *</label>
                          <input type="text" value={patientForm.subscriber_first_name}
                            onChange={(e) => setPatientForm({ ...patientForm, subscriber_first_name: e.target.value })} required />
                        </div>
                        <div className="form-field" style={{ flex: 2 }}>
                          <label>Last Name *</label>
                          <input type="text" value={patientForm.subscriber_last_name}
                            onChange={(e) => setPatientForm({ ...patientForm, subscriber_last_name: e.target.value })} required />
                        </div>
                        <div className="form-field" style={{ flex: 1 }}>
                          <label>DOB</label>
                          <input type="text" placeholder="M/D/YYYY" value={patientForm.subscriber_dob}
                            onChange={(e) => setPatientForm({ ...patientForm, subscriber_dob: e.target.value })} />
                        </div>
                        <div className="form-field" style={{ flex: 1 }}>
                          <label>Gender</label>
                          <select value={patientForm.subscriber_gender}
                            onChange={(e) => setPatientForm({ ...patientForm, subscriber_gender: e.target.value })}>
                            <option value="">-</option>
                            <option value="M">Male</option>
                            <option value="F">Female</option>
                          </select>
                        </div>
                      </div>

                      <div className="form-row-inline">
                        <div className="form-field">
                          <label>Phone</label>
                          <input type="text" value={patientForm.phone}
                            onChange={(e) => setPatientForm({ ...patientForm, phone: e.target.value })} />
                        </div>
                        <div className="form-field">
                          <label>Email</label>
                          <input type="text" value={patientForm.email}
                            onChange={(e) => setPatientForm({ ...patientForm, email: e.target.value })} />
                        </div>
                      </div>

                      <div className="form-row-inline">
                        <div className="form-field" style={{ flex: 2 }}>
                          <label>Address</label>
                          <input type="text" value={patientForm.address1}
                            onChange={(e) => setPatientForm({ ...patientForm, address1: e.target.value })} />
                        </div>
                        <div className="form-field" style={{ flex: 1 }}>
                          <label>City</label>
                          <input type="text" value={patientForm.city}
                            onChange={(e) => setPatientForm({ ...patientForm, city: e.target.value })} />
                        </div>
                        <div className="form-field" style={{ flex: 0, minWidth: '60px' }}>
                          <label>State</label>
                          <input type="text" maxLength={2} value={patientForm.state}
                            onChange={(e) => setPatientForm({ ...patientForm, state: e.target.value.toUpperCase() })} />
                        </div>
                        <div className="form-field" style={{ flex: 0, minWidth: '80px' }}>
                          <label>ZIP</label>
                          <input type="text" maxLength={10} value={patientForm.zip}
                            onChange={(e) => setPatientForm({ ...patientForm, zip: e.target.value })} />
                        </div>
                      </div>

                      {patientForm.patient_is_subscriber === 'N' && (
                        <div className="form-row-inline" style={{ marginTop: '8px', padding: '8px', background: '#f8f9fa', borderRadius: '4px' }}>
                          <div className="form-field" style={{ flex: 2 }}>
                            <label>Dependent First</label>
                            <input type="text" value={patientForm.patient_first_name}
                              onChange={(e) => setPatientForm({ ...patientForm, patient_first_name: e.target.value })} />
                          </div>
                          <div className="form-field" style={{ flex: 2 }}>
                            <label>Dependent Last</label>
                            <input type="text" value={patientForm.patient_last_name}
                              onChange={(e) => setPatientForm({ ...patientForm, patient_last_name: e.target.value })} />
                          </div>
                          <div className="form-field" style={{ flex: 1 }}>
                            <label>Relationship</label>
                            <select value={patientForm.patient_relationship_code}
                              onChange={(e) => setPatientForm({ ...patientForm, patient_relationship_code: e.target.value })}>
                              <option value="19">Child</option>
                              <option value="01">Spouse</option>
                              <option value="18">Self</option>
                              <option value="G8">Other</option>
                            </select>
                          </div>
                        </div>
                      )}

                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '8px', fontSize: '13px', cursor: 'pointer' }}>
                        <input type="checkbox"
                          checked={patientForm.patient_is_subscriber === 'N'}
                          onChange={(e) => setPatientForm({ ...patientForm, patient_is_subscriber: e.target.checked ? 'N' : 'Y' })}
                          style={{ margin: 0 }} />
                        Patient is a dependent (not the subscriber)
                      </label>
                    </div>

                    {/* === PAYMENT INFO === */}
                    <div className="patient-section">
                      <div className="patient-section-title">Payment Info</div>

                      <div className="form-field" style={{ marginBottom: '12px' }}>
                        <label>Pay Type</label>
                        <div className="pay-type-buttons">
                          {[
                            { value: 'insurance', label: 'Insurance' },
                            { value: 'self_pay', label: 'Self-Pay' },
                            { value: 'auto_wc', label: 'Auto/WC' },
                            { value: 'other', label: 'Other' },
                            { value: 'unknown', label: 'Unknown' },
                          ].map((opt) => (
                            <button
                              key={opt.value}
                              type="button"
                              className={`pay-type-btn ${patientForm.payment_type === opt.value ? 'active' : ''}`}
                              onClick={() => setPatientForm({ ...patientForm, payment_type: opt.value })}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {(patientForm.payment_type === 'insurance' || patientForm.payment_type === 'unknown') && (
                        <>
                          <div className="insurance-table">
                            <div className="insurance-table-header">
                              <div className="insurance-seq">Sequence</div>
                              <div className="insurance-payer">Payer</div>
                              <div className="insurance-member">Member ID</div>
                            </div>

                            {/* Primary */}
                            <div className="insurance-row">
                              <div className="insurance-seq">Primary</div>
                              <div className="insurance-payer">
                                <select
                                  value={patientForm.payer_id}
                                  onChange={(e) => {
                                    const selectedPayer = configuredPayers.find((p) => p.payer_id === e.target.value);
                                    setPatientForm({
                                      ...patientForm,
                                      payer_id: selectedPayer?.payer_id || '',
                                      payer_name: selectedPayer?.payer_name || '',
                                    });
                                  }}
                                >
                                  <option value="">— none —</option>
                                  {configuredPayers.map((p) => (
                                    <option key={p.id} value={p.payer_id}>{p.payer_name}</option>
                                  ))}
                                </select>
                              </div>
                              <div className="insurance-member">
                                <input type="text" value={patientForm.subscriber_id}
                                  onChange={(e) => setPatientForm({ ...patientForm, subscriber_id: e.target.value })} />
                              </div>
                            </div>

                            {/* Secondary */}
                            <div className="insurance-row">
                              <div className="insurance-seq">Secondary</div>
                              <div className="insurance-payer">
                                <select
                                  value={patientForm.secondary_payer_id}
                                  onChange={(e) => {
                                    const selectedPayer = configuredPayers.find((p) => p.payer_id === e.target.value);
                                    setPatientForm({
                                      ...patientForm,
                                      secondary_payer_id: selectedPayer?.payer_id || '',
                                      secondary_payer_name: selectedPayer?.payer_name || '',
                                    });
                                  }}
                                >
                                  <option value="">— none —</option>
                                  {configuredPayers.map((p) => (
                                    <option key={p.id} value={p.payer_id}>{p.payer_name}</option>
                                  ))}
                                </select>
                              </div>
                              <div className="insurance-member">
                                <input type="text" value={patientForm.secondary_subscriber_id}
                                  onChange={(e) => setPatientForm({ ...patientForm, secondary_subscriber_id: e.target.value })} />
                              </div>
                            </div>

                            {/* Tertiary */}
                            <div className="insurance-row">
                              <div className="insurance-seq">Tertiary</div>
                              <div className="insurance-payer">
                                <select
                                  value={patientForm.tertiary_payer_id}
                                  onChange={(e) => {
                                    const selectedPayer = configuredPayers.find((p) => p.payer_id === e.target.value);
                                    setPatientForm({
                                      ...patientForm,
                                      tertiary_payer_id: selectedPayer?.payer_id || '',
                                      tertiary_payer_name: selectedPayer?.payer_name || '',
                                    });
                                  }}
                                >
                                  <option value="">— none —</option>
                                  {configuredPayers.map((p) => (
                                    <option key={p.id} value={p.payer_id}>{p.payer_name}</option>
                                  ))}
                                </select>
                              </div>
                              <div className="insurance-member">
                                <input type="text" value={patientForm.tertiary_subscriber_id}
                                  onChange={(e) => setPatientForm({ ...patientForm, tertiary_subscriber_id: e.target.value })} />
                              </div>
                            </div>
                          </div>

                          {!editingPatient?._isNew && editingPatient?.payer_id && !patientForm.payer_id && (
                            <small className="text-muted">
                              Existing payer is not configured for this practice. Pick one from the list to replace it.
                            </small>
                          )}

                          {!editingPatient?._isNew && editingPatient?.payer_id && (
                            <div className="form-field" style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <label style={{ minWidth: '140px', marginBottom: 0 }}>Estimated Copay</label>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <span style={{ fontSize: '14px', color: '#64748b' }}>$</span>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  placeholder="—"
                                  value={patientForm.estimated_copay || ''}
                                  onChange={(e) => setPatientForm({ ...patientForm, estimated_copay: e.target.value })}
                                  style={{ width: '90px' }}
                                />
                                {editingPatient?.estimated_copay_status === 'manual_override' && (
                                  <span style={{ fontSize: '11px', padding: '2px 6px', background: '#dbeafe', color: '#1e40af', borderRadius: '3px', textTransform: 'uppercase', fontWeight: 600 }}>Manual</span>
                                )}
                                {editingPatient?.estimated_copay_status === 'active' && editingPatient?.estimated_copay_source === 'claim_seed' && (
                                  <span style={{ fontSize: '11px', padding: '2px 6px', background: '#dcfce7', color: '#166534', borderRadius: '3px', textTransform: 'uppercase', fontWeight: 600 }}>
                                    Auto · {editingPatient?.estimated_copay_evidence_n || 0} visits
                                  </span>
                                )}
                              </div>
                              {editingPatient?.estimated_copay_status && (
                                <small className="text-muted" style={{ flex: 1 }}>
                                  Tied to {patientForm.payer_name || 'current insurance'}. Wiped if insurance changes.
                                </small>
                              )}
                            </div>
                          )}
                        </>
                      )}

                      {/* Cards on File */}
                      {!readOnly && (
                        <div style={{ marginTop: '16px' }}>
                          <div className="insurance-table-header" style={{ display: 'flex', padding: '6px 8px', fontWeight: 600, fontSize: '12px', textTransform: 'uppercase', color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>
                            <div style={{ flex: 1 }}>Cards on File</div>
                          </div>
                          {(editingPatient?.cards && editingPatient.cards.length > 0) ? (
                            editingPatient.cards.map((card, i) => (
                              <div key={i} className="insurance-row" style={{ padding: '8px' }}>
                                <div style={{ flex: 1 }}>
                                  {card.card_type} ••••{card.last4}
                                  {card.exp_month && card.exp_year && (
                                    <span style={{ color: '#64748b', marginLeft: '8px' }}>
                                      exp {String(card.exp_month).padStart(2, '0')}/{String(card.exp_year).slice(-2)}
                                    </span>
                                  )}
                                  {card.is_default && (
                                    <span style={{ marginLeft: '8px', fontSize: '11px', background: '#dbeafe', color: '#1e40af', padding: '2px 6px', borderRadius: '3px' }}>Default</span>
                                  )}
                                  {card.is_expired && (
                                    <span style={{ marginLeft: '8px', fontSize: '11px', background: '#fee2e2', color: '#991b1b', padding: '2px 6px', borderRadius: '3px' }}>Expired</span>
                                  )}
                                </div>
                              </div>
                            ))
                          ) : (
                            <div style={{ padding: '8px', fontSize: '13px', color: '#94a3b8', fontStyle: 'italic' }}>
                              No cards on file
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Guarantor — collapsed by default, lazy-loaded on expand. Existing patients only. */}
                    {editingPatient?.id && !editingPatient?._isNew && (
                      <div className="patient-section">
                        <div
                          className="patient-section-title"
                          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
                          onClick={toggleGuarantorPanel}
                        >
                          <span>Guarantor</span>
                          <span style={{ fontSize: '12px', color: '#64748b', textTransform: 'none', fontWeight: 400 }}>
                            {guarantorSummary
                              ? (guarantorSummary.relationship_to_patient === 'self'
                                  ? 'Self'
                                  : `${guarantorSummary.relationship_to_patient}${(guarantorSummary.first_name || guarantorSummary.last_name) ? ` — ${guarantorSummary.first_name} ${guarantorSummary.last_name}`.trimEnd() : ''}`)
                              : 'Self'}
                            <span style={{ marginLeft: '8px' }}>{guarantorOpen ? '▾' : '▸'}</span>
                          </span>
                        </div>

                        {guarantorOpen && (
                          <div style={{ marginTop: '8px' }}>
                            {guarantorLoading && <div style={{ fontSize: '13px', color: '#94a3b8' }}>Loading…</div>}
                            {guarantorMsg && (
                              <div className={`form-message ${guarantorMsg.type}`} style={{ marginBottom: '8px' }}>{guarantorMsg.text}</div>
                            )}
                            {guarantorForm && !guarantorLoading && (
                              <>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                                  <div className="form-field" style={{ flex: 1, marginBottom: 0 }}>
                                    <select
                                      value={guarantorForm.relationship_to_patient}
                                      disabled={readOnly}
                                      onChange={(e) => {
                                        const rel = e.target.value;
                                        setGuarantorForm((prev) => {
                                          const wasSelf = prev.relationship_to_patient === 'self';
                                          const next = {
                                            ...prev,
                                            relationship_to_patient: rel,
                                            mode: rel === 'self' ? 'self' : (prev.source_patient_id ? 'link' : 'manual'),
                                          };
                                          // First time leaving "self": don't carry the patient's own
                                          // demographics into the manual form — start blank.
                                          if (wasSelf && rel !== 'self') {
                                            Object.assign(next, {
                                              first_name: '', last_name: '', dob: '',
                                              address1: '', address2: '', city: '', state: '', zip: '',
                                              phone: '', email: '', notes: '',
                                              source_patient_id: null, source_patient_name: '',
                                            });
                                          }
                                          return next;
                                        });
                                      }}
                                    >
                                      <option value="self">Self (patient pays own bills)</option>
                                      <option value="parent">Parent</option>
                                      <option value="spouse">Spouse</option>
                                      <option value="conservator">Conservator</option>
                                      <option value="estate">Estate</option>
                                      <option value="other">Other</option>
                                    </select>
                                  </div>
                                  {guarantorForm.relationship_to_patient !== 'self' && (
                                    <>
                                      <button type="button" className={`btn ${guarantorForm.mode === 'link' ? 'btn-primary' : 'btn-secondary'}`} disabled={readOnly} onClick={() => updateGuarantorField('mode', 'link')}>Link existing patient</button>
                                      <button type="button" className={`btn ${guarantorForm.mode === 'manual' ? 'btn-primary' : 'btn-secondary'}`} disabled={readOnly} onClick={() => updateGuarantorField('mode', 'manual')}>Enter manually</button>
                                    </>
                                  )}
                                </div>

                                {guarantorForm.relationship_to_patient === 'self' ? (
                                  <div style={{ fontSize: '13px', color: '#64748b', padding: '4px 0' }}>
                                    The patient is responsible for their own bills — no extra details needed.
                                  </div>
                                ) : (
                                  <>

                                    {guarantorForm.mode === 'link' ? (
                                      <div>
                                        {guarantorForm.source_patient_id ? (
                                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', padding: '8px 12px', background: '#f1f5f9', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', fontSize: '14px', color: 'var(--text-primary)' }}>
                                            <span>{guarantorForm.source_patient_name || `${guarantorForm.first_name || ''} ${guarantorForm.last_name || ''}`.trim()}</span>
                                            {!readOnly && (
                                              <button
                                                type="button"
                                                aria-label="Unlink patient"
                                                onClick={() => { setGuarantorForm((prev) => ({ ...prev, source_patient_id: null, source_patient_name: '' })); setGuarantorPatientSearch(''); }}
                                                style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '18px', lineHeight: 1, color: 'var(--text-secondary)', padding: '0 2px' }}
                                              >×</button>
                                            )}
                                          </div>
                                        ) : (
                                          <>
                                            <div className="form-field" style={{ marginBottom: '6px' }}>
                                              <input
                                                type="text"
                                                placeholder="Search this practice's patients by name…"
                                                value={guarantorPatientSearch}
                                                disabled={readOnly}
                                                onChange={(e) => setGuarantorPatientSearch(e.target.value)}
                                              />
                                            </div>
                                            <div style={{ minHeight: '80px', maxHeight: '140px', overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)' }}>
                                              {guarantorPatientSearch.trim() === '' ? null : (
                                                patients
                                                  .filter((p) => p.id !== editingPatient.id)
                                                  .filter((p) => (p.display_name || '').toLowerCase().includes(guarantorPatientSearch.trim().toLowerCase()))
                                                  .slice(0, 20)
                                                  .map((p, idx) => (
                                                    <div
                                                      key={p.id}
                                                      onClick={() => { if (!readOnly) { setGuarantorForm((prev) => ({ ...prev, source_patient_id: p.id, source_patient_name: p.display_name })); setGuarantorPatientSearch(''); } }}
                                                      style={{ padding: '8px 12px', cursor: 'pointer', fontSize: '14px', borderTop: idx === 0 ? 'none' : '1px solid var(--border-color)' }}
                                                    >
                                                      {p.display_name}{p.patient_dob ? ` (${p.patient_dob})` : ''}
                                                    </div>
                                                  ))
                                              )}
                                            </div>
                                          </>
                                        )}
                                      </div>
                                    ) : (
                                      <>
                                        <div className="form-row-inline">
                                          <div className="form-field" style={{ flex: 2 }}><label>First name</label><input type="text" value={guarantorForm.first_name} disabled={readOnly} onChange={(e) => updateGuarantorField('first_name', e.target.value)} /></div>
                                          <div className="form-field" style={{ flex: 2 }}><label>Last name</label><input type="text" value={guarantorForm.last_name} disabled={readOnly} onChange={(e) => updateGuarantorField('last_name', e.target.value)} /></div>
                                          <div className="form-field" style={{ flex: 1 }}><label>DOB</label><input type="text" placeholder="M/D/YYYY" value={guarantorForm.dob} disabled={readOnly} onChange={(e) => updateGuarantorField('dob', e.target.value)} /></div>
                                        </div>
                                        <div className="form-row-inline">
                                          <div className="form-field" style={{ flex: 1 }}><label>Address 1</label><input type="text" value={guarantorForm.address1} disabled={readOnly} onChange={(e) => updateGuarantorField('address1', e.target.value)} /></div>
                                          <div className="form-field" style={{ flex: 1 }}><label>Address 2</label><input type="text" value={guarantorForm.address2} disabled={readOnly} onChange={(e) => updateGuarantorField('address2', e.target.value)} /></div>
                                        </div>
                                        <div className="form-row-inline">
                                          <div className="form-field" style={{ flex: 2 }}><label>City</label><input type="text" value={guarantorForm.city} disabled={readOnly} onChange={(e) => updateGuarantorField('city', e.target.value)} /></div>
                                          <div className="form-field" style={{ flex: 1 }}><label>State</label><input type="text" maxLength={2} value={guarantorForm.state} disabled={readOnly} onChange={(e) => updateGuarantorField('state', e.target.value)} /></div>
                                          <div className="form-field" style={{ flex: 1 }}><label>ZIP</label><input type="text" value={guarantorForm.zip} disabled={readOnly} onChange={(e) => updateGuarantorField('zip', e.target.value)} /></div>
                                        </div>
                                        <div className="form-row-inline">
                                          <div className="form-field" style={{ flex: 1 }}><label>Phone</label><input type="text" value={guarantorForm.phone} disabled={readOnly} onChange={(e) => updateGuarantorField('phone', e.target.value)} /></div>
                                          <div className="form-field" style={{ flex: 2 }}><label>Email</label><input type="text" value={guarantorForm.email} disabled={readOnly} onChange={(e) => updateGuarantorField('email', e.target.value)} /></div>
                                        </div>
                                      </>
                                    )}
                                  </>
                                )}

                                {!readOnly && (
                                  <div style={{ marginTop: '8px' }}>
                                    <button type="button" className="btn btn-primary" disabled={guarantorSaving} onClick={handleSaveGuarantor}>
                                      {guarantorSaving ? 'Saving…' : 'Save guarantor'}
                                    </button>
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    </fieldset>
                    <div className="modal-actions">
                      <button type="button" className="btn btn-secondary" onClick={() => setEditingPatient(null)}>{readOnly ? 'Close' : 'Cancel'}</button>
                      {!readOnly && (
                        <button type="submit" className="btn btn-primary" disabled={savingPatient}>
                          {savingPatient ? 'Saving...' : 'Save'}
                        </button>
                      )}
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Outreach templates (staff only; the texts, emails and call scripts for balances) */}
        {!readOnly && activeTab === 'outreachTemplates' && (
          <TemplatesTab practiceId={selectedPractice.id} isSuperAdmin={isSuperAdmin} />
        )}

        {/* Fee Schedule Tab */}
        {isAdmin && activeTab === 'feeSchedule' && (
          // The same page the old Setup > Fee Schedule nav entry opened, now a tab here
          // (David, 2026-10-25: "Fee schedule should be included in Practice Details").
          <FeeSchedulePage selectedPractice={selectedPractice} readOnly={readOnly} />
        )}

        {/* Practice Rules Tab */}
        {activeTab === 'billingSettings' && (
          <div className="info-tab practice-rules-tab">
            <CodingStandardsSection practiceId={selectedPractice.id} isAdmin={isAdmin} readOnly={readOnly} />
            {/* One form, one Save (2026-10-25): card payments, posting and the eCW status
                codes, each part behind its own role gate. The charge templates moved to
                the Charges page. Not shown to the clinic. */}
            {!readOnly && (
              <BillingSettingsForm
                practiceId={selectedPractice.id}
                isAdmin={isAdmin}
                isSuperAdmin={isSuperAdmin}
                isEcw={practice?.emr_system === 'ecw'}
              />
            )}
          </div>
        )}

        {!readOnly && activeTab === 'notifications' && (
          <div className="info-tab practice-rules-tab">
            {/* Every email or report this practice gets, in one tab (2026-10-25). */}
            {isAdmin && (
            <>
            {/* Daily email: the morning email and the evening copay email, one Save. */}
            <div className="integrations-section">
              <h3
                className="practice-rules-section-title"
                style={{ margin: '0 0 1rem' }}
              >
                Daily email
              </h3>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  marginBottom: '12px',
                }}
              >
                <label style={{ width: 180, color: 'var(--text-secondary)', margin: 0, fontSize: 13, fontWeight: 600 }}>
                  Enabled?
                </label>
                <input
                  type="checkbox"
                  checked={!!copayConfigForm.copay_email_enabled}
                  onChange={(e) =>
                    setCopayConfigForm({
                      ...copayConfigForm,
                      copay_email_enabled: e.target.checked,
                    })
                  }
                  disabled={readOnly}
                  style={{ margin: 0 }}
                />
              </div>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  marginBottom: '12px',
                }}
              >
                <label style={{ width: 180, color: 'var(--text-secondary)', margin: 0, fontSize: 13, fontWeight: 600 }}>
                  Recipients
                </label>
                <input
                  type="text"
                  value={copayConfigForm.copay_email_recipients}
                  onChange={(e) =>
                    setCopayConfigForm({
                      ...copayConfigForm,
                      copay_email_recipients: e.target.value,
                    })
                  }
                  placeholder="david@atlasbillers.com, frontdesk@…"
                  disabled={readOnly}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    border: '1px solid var(--border-color)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 14,
                    color: 'var(--text-primary)',
                    background: 'var(--bg-primary)',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  marginBottom: '12px',
                }}
              >
                <label style={{ width: 180, color: 'var(--text-secondary)', margin: 0, fontSize: 13, fontWeight: 600 }}>
                  Eligibility Lookahead
                </label>
                <input
                  type="number"
                  min={0}
                  max={14}
                  value={copayConfigForm.copay_email_lookahead_days}
                  onChange={(e) =>
                    setCopayConfigForm({
                      ...copayConfigForm,
                      copay_email_lookahead_days: e.target.value,
                    })
                  }
                  disabled={readOnly}
                  style={{
                    width: 80,
                    padding: '8px 12px',
                    border: '1px solid var(--border-color)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 14,
                    color: 'var(--text-primary)',
                    background: 'var(--bg-primary)',
                    fontFamily: 'inherit',
                    boxSizing: 'border-box',
                  }}
                />
                <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>days</span>
              </div>

              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  marginBottom: '12px',
                }}
              >
                <label style={{ width: 180, color: 'var(--text-secondary)', margin: 0, fontSize: 13, fontWeight: 600 }}>
                  Send On
                </label>
                <div style={{ display: 'flex', gap: '0.25rem' }}>
                  {[
                    { d: 1, label: 'Mon' },
                    { d: 2, label: 'Tue' },
                    { d: 3, label: 'Wed' },
                    { d: 4, label: 'Thu' },
                    { d: 5, label: 'Fri' },
                    { d: 6, label: 'Sat' },
                    { d: 7, label: 'Sun' },
                  ].map(({ d, label }) => {
                    const selected = (
                      copayConfigForm.copay_email_schedule_days || []
                    ).includes(d);
                    return (
                      <label
                        key={d}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.25rem',
                          margin: 0,
                          padding: '0.15rem 0.4rem',
                          fontSize: 13,
                          fontWeight: 'normal',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selected}
                          onChange={(e) => {
                            const current = new Set(
                              copayConfigForm.copay_email_schedule_days || []
                            );
                            if (e.target.checked) current.add(d);
                            else current.delete(d);
                            setCopayConfigForm({
                              ...copayConfigForm,
                              copay_email_schedule_days: Array.from(current).sort(
                                (a, b) => a - b
                              ),
                            });
                          }}
                          disabled={readOnly}
                          style={{ margin: 0 }}
                        />
                        {label}
                      </label>
                    );
                  })}
                </div>
              </div>

              {!readOnly && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    marginBottom: '12px',
                  }}
                >
                  <label style={{ width: 180, margin: 0 }}>{' '}</label>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={handlePreviewCopayEmail}
                    disabled={previewingCopayEmail}
                    title="Sends today's daily email to your own address now, whatever the recipient list and the Enabled switch say"
                  >
                    {previewingCopayEmail ? 'Sending…' : 'Send sample to me'}
                  </button>
                  {copaySampleMessage && (
                    <span
                      className={`form-message ${copaySampleMessage.type}`}
                      data-testid="copay-sample-message"
                      style={{ margin: 0 }}
                    >
                      {copaySampleMessage.text}
                      <button type="button" className="weekly-reports-dismiss" onClick={() => setCopaySampleMessage(null)} aria-label="Dismiss">×</button>
                    </span>
                  )}
                </div>
              )}

            </div>
            </>
            )}
            {/* Weekly report: admin only (the backend refuses everyone else), with the
                star ratings its recipients sent back inside it. Other staff see the
                feedback alone. */}
            {isAdmin ? (
              <WeeklyReportsCard ref={weeklyReportsRef} practiceId={selectedPractice.id} readOnly={readOnly} showSave={false}>
                <ClientFeedbackCard practiceId={selectedPractice.id} embedded />
              </WeeklyReportsCard>
            ) : (
              <ClientFeedbackCard practiceId={selectedPractice.id} />
            )}

            {/* One Save for the whole tab (admins: everyone else has nothing to edit). */}
            {isAdmin && (
              <div className="notifications-save">
                {copayConfigMessage && (
                  <div className={`form-message ${copayConfigMessage.type}`}>
                    {copayConfigMessage.text}
                    <button type="button" className="weekly-reports-dismiss" onClick={() => setCopayConfigMessage(null)} aria-label="Dismiss">×</button>
                  </div>
                )}
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveNotifications}
                  disabled={savingCopayConfig}
                >
                  {savingCopayConfig ? 'Saving…' : 'Save settings'}
                </button>
              </div>
            )}
          </div>
        )}

        {isAdmin && activeTab === 'features' && (
          <div className="info-tab practice-rules-tab">
            {/* Which pages are on for this practice: Auto (from what its feeds supply),
                or forced On / Off. */}
            <IntegrationsFeaturesPanel
              practiceId={selectedPractice.id}
              practice={practice}
              isAdmin={isAdmin}
              switchesOnly
            />
          </div>
        )}

      </div>
    </div>
  );
}

export default PracticeDetailsPage;
